import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { apiFetch } from '../bizsuite-app';
import { useRoleOptions } from '../hooks/useRoleOptions';
import Modal from './Modal';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';
import { formatDate } from '../utils/date';
import * as XLSX from 'xlsx';

/* ─── Default Settings ─── */
/** Today's date in YYYY-MM-DD (used as default start date). Evaluated each
 * mount so a freshly-opened cost sheet always starts "today". */
function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
const DEFAULT_SETTINGS = {
  billingCurrency: 'INR',
  displayCurrency: 'INR',
  mandaysPerMonth: 20,
  hoursPerDay: 8,
  defaultOffshoreRate: 0,
  defaultOnsiteRate: 0,
  exchangeRate: 94,
  visaType: 'Business',
  startDate: todayISO(),
  discountPct: 0,
  months: 6,
  productId: '',
  overheadPct: 0,
  rateScale: '100000',       // "L ('00,000)" — Indian Lakhs scale
  ratePer: 'ManMonth',       // ManMonth | ManWeek | ManDay | ManHour
  effortUnit: 'month',       // month | week
  showActualPeriod: false,
  // Default extras (applied to new lines) — all 0 per user preference
  defaultPerDiem: 0,
  defaultVisaRate: 0,
  defaultConveyance: 0,
  defaultLodging: 0,
  defaultBoarding: 0,
};

// The role list comes from useRoleOptions() — single source of truth is
// Administration → Metadata → Lookups → Role (metadata_lookups category='role').
const PRICE_TYPE = ['Included', 'Excluded'];
const ACCOMM_OPTIONS = ['Self', 'Company Provided', 'Client Provided'];
const SOURCE_OPTIONS = ['Internal', 'External', 'Other'];
const CURRENCY_OPTIONS = ['USD', 'EUR', 'GBP', 'INR', 'AED', 'SGD', 'SEK', 'NOK'];
const LEVEL_OPTIONS = ['', 'L1', 'L2', 'L3', 'L4', 'L5'];

/* ─── Rate Per & Effort Unit ─── */
const RATE_PER_OPTIONS = [
  { value: 'ManMonth', label: 'Man-Month' },
  { value: 'ManWeek',  label: 'Man-Week' },
  { value: 'ManDay',   label: 'Man-Day' },
  { value: 'ManHour',  label: 'Man-Hour' },
];
const EFFORT_UNIT_OPTIONS = [
  { value: 'month', label: 'Months' },
  { value: 'week',  label: 'Weeks' },
];
const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/** Number of periods based on effortUnit */
function getPeriodCount(s) {
  const m = s.months || 3;
  return (s.effortUnit === 'week') ? m * 4 : m;
}

/** Working days per period */
function mandaysInPeriod(s) {
  return (s.effortUnit === 'week') ? 5 : (s.mandaysPerMonth || 20);
}

/** Convert a rate (entered in ratePer unit) to hourly equivalent */
function rateToHourly(rate, s) {
  const r = parseFloat(rate) || 0;
  const md = s.mandaysPerMonth || 20;
  const hd = s.hoursPerDay || 8;
  switch (s.ratePer || 'ManHour') {
    case 'ManMonth': return r / (md * hd);
    case 'ManWeek':  return r / (5 * hd);
    case 'ManDay':   return r / hd;
    case 'ManHour': default: return r;
  }
}

/** Column header for a given period index */
function getPeriodLabel(index, s) {
  const isWeek = s.effortUnit === 'week';
  if (!s.showActualPeriod || !s.startDate) {
    return isWeek ? `W${index + 1}` : `M${index + 1}`;
  }
  const start = new Date(s.startDate);
  if (isNaN(start.getTime())) return isWeek ? `W${index + 1}` : `M${index + 1}`;
  if (isWeek) {
    const d = new Date(start);
    d.setDate(d.getDate() + index * 7);
    return `${d.getDate()}-${MONTH_ABBR[d.getMonth()]}`;
  } else {
    const d = new Date(start.getFullYear(), start.getMonth() + index, 1);
    return `${MONTH_ABBR[d.getMonth()]}-${String(d.getFullYear()).slice(-2)}`;
  }
}

/** Max FTE for a period (first period is partial if start date is mid-period) */
function getMaxFteForPeriod(periodIdx, fte, s) {
  const maxFte = parseFloat(fte) || 0;
  if (!s.startDate || periodIdx > 0) return maxFte;
  const start = new Date(s.startDate);
  if (isNaN(start.getTime())) return maxFte;
  if ((s.effortUnit || 'month') === 'week') {
    const dow = start.getDay(); // 0=Sun … 6=Sat
    const workDaysLeft = (dow === 0 || dow === 6) ? 5 : (6 - dow); // Mon→5, Tue→4 …
    return Math.round(maxFte * workDaysLeft / 5 * 100) / 100;
  } else {
    const dayOfMonth = start.getDate();
    const daysInMonth = new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();
    return Math.round(maxFte * (daysInMonth - dayOfMonth + 1) / daysInMonth * 100) / 100;
  }
}

/* ─── Rate Scale (display multiplier) ─── */
const SCALE_OPTIONS = [
  { value: '1',       label: 'Absolute' },
  { value: '1000',    label: "K ('000)" },
  { value: '100000',  label: "L ('00,000)" },
  { value: '1000000', label: "M ('000,000)" },
  { value: '10000000', label: "Cr ('00,00,000)" },
];
function scaleDivisor(s) { return parseFloat(s) || 1; }
function scaleLabel(s) {
  const opt = SCALE_OPTIONS.find(o => o.value === String(s));
  if (!opt || s === '1' || s === 1) return '';
  return opt.label.split(' ')[0]; // 'K', 'L', 'M', 'Cr'
}
/** Convert a display-scale value to absolute */
function toAbsolute(displayVal, scale) { return (parseFloat(displayVal) || 0) * scaleDivisor(scale); }
/** Convert an absolute value to display-scale */
function toDisplay(absoluteVal, scale) {
  const d = scaleDivisor(scale);
  return d === 1 ? absoluteVal : absoluteVal / d;
}
/** Format number with scale suffix + currency symbol, e.g. "10.0 K$" */
function fmtScaled(absoluteVal, scale, sym, decimals) {
  const d = scaleDivisor(scale);
  const lbl = scaleLabel(scale);
  const dec = decimals != null ? decimals : (d === 1 ? 0 : 1);
  const v = d === 1 ? absoluteVal : absoluteVal / d;
  return `${fmtNum(v, dec)} ${lbl}${sym}`.trim();
}

function emptyResource(periodCount, s) {
  const r = {
    role: 'Consultant', level: '', priceType: 'Included', accommodation: 'Self',
    duration: periodCount, source: 'Internal', fte: 1,
    offshoreRate: (s && s.defaultOffshoreRate != null) ? s.defaultOffshoreRate : 0,
    onsiteRate:   (s && s.defaultOnsiteRate   != null) ? s.defaultOnsiteRate   : 0,
    productId: '', moduleId: '',
    employeeIds: [],
    offshoreMonths: [], onsiteMonths: [], returnTravel: 0,
    perDiemRate:   (s && s.defaultPerDiem    != null) ? s.defaultPerDiem    : 0,
    visaRate:      (s && s.defaultVisaRate   != null) ? s.defaultVisaRate   : 0,
    conveyanceRate:(s && s.defaultConveyance != null) ? s.defaultConveyance : 0,
    lodgingRate:   (s && s.defaultLodging    != null) ? s.defaultLodging    : 0,
    boardingRate:  (s && s.defaultBoarding   != null) ? s.defaultBoarding   : 0,
  };
  // Default M1..Mn: Onsite = 1, Offshore = 0
  for (let i = 0; i < periodCount; i++) { r.offshoreMonths.push(0); r.onsiteMonths.push(1); }
  return r;
}

/* ─── Calculation helpers ─── */
function calcOffshoreHrs(res, s) {
  return res.offshoreMonths.reduce((a, v) => a + (parseFloat(v) || 0), 0) * mandaysInPeriod(s) * (s.hoursPerDay || 8);
}
function calcOnsiteHrs(res, s) {
  return res.onsiteMonths.reduce((a, v) => a + (parseFloat(v) || 0), 0) * mandaysInPeriod(s) * (s.hoursPerDay || 8);
}
function calcOffshoreAmt(res, s) {
  return rateToHourly(res.offshoreRate, s) * calcOffshoreHrs(res, s);
}
function calcOnsiteAmt(res, s) {
  return rateToHourly(res.onsiteRate, s) * calcOnsiteHrs(res, s);
}
function calcAmount(res, s) {
  return calcOffshoreAmt(res, s) + calcOnsiteAmt(res, s);
}
function calcPerDiem(res, s) {
  const onsH = calcOnsiteHrs(res, s);
  return (parseFloat(res.perDiemRate) || 0) * (onsH / (s.hoursPerDay || 8));
}
function calcVisa(res) {
  return (parseFloat(res.visaRate) || 0) * (parseFloat(res.returnTravel) || 0);
}
function calcLodging(res, s) {
  const onsH = calcOnsiteHrs(res, s);
  const onsDays = onsH / (s.hoursPerDay || 8);
  const onsMonths = onsDays / (s.mandaysPerMonth || 20);
  return onsMonths * 30 * (parseFloat(res.lodgingRate) || 0);
}
function calcBoarding(res, s) {
  const onsH = calcOnsiteHrs(res, s);
  const onsDays = onsH / (s.hoursPerDay || 8);
  const onsMonths = onsDays / (s.mandaysPerMonth || 20);
  return onsMonths * 30 * (parseFloat(res.boardingRate) || 0);
}
function calcExtras(res, s) {
  return calcPerDiem(res, s) + calcVisa(res) + (parseFloat(res.conveyanceRate) || 0) + calcLodging(res, s) + calcBoarding(res, s);
}

function fmtNum(v, decimals = 0) {
  if (v == null || isNaN(v)) return '—';
  return parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/* ─── Compact multi-select (tags + checklist dropdown) used in table cells
       for Skill Groups and Products where more than one value can be chosen. */
function MultiTagSelect({ value, onChange, options, placeholder = '— None —', width, readOnly }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selectedIds = Array.isArray(value) ? value : (value ? [value] : []);

  useEffect(() => {
    function h(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    if (open) document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const byId = Object.fromEntries((options || []).map(o => [o.value, o.label]));
  const labelText = selectedIds.length === 0
    ? placeholder
    : selectedIds.length === 1
      ? (byId[selectedIds[0]] || '1 selected')
      : `${selectedIds.length} selected`;

  if (readOnly) {
    return (
      <span className="cs-readonly" style={{ width, textAlign: 'right' }}>
        {selectedIds.length === 0 ? '—' : selectedIds.map(id => byId[id]).filter(Boolean).join(', ')}
      </span>
    );
  }

  function toggle(id) {
    if (selectedIds.includes(id)) onChange(selectedIds.filter(x => x !== id));
    else onChange([...selectedIds, id]);
  }

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block', width }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="cs-input"
        style={{
          width: width || 120, textAlign: 'left', cursor: 'pointer',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          padding: '5px 8px',
        }}
        title={selectedIds.map(id => byId[id]).filter(Boolean).join(', ') || placeholder}
      >
        {labelText} <span style={{ opacity: 0.6, marginLeft: 4 }}>▾</span>
      </button>
      {open && (
        <div
          onClick={e => e.stopPropagation()}
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 1000,
            minWidth: 220, maxHeight: 280, overflowY: 'auto',
            background: 'rgba(15,23,42,0.98)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 8, padding: 4,
            boxShadow: '0 12px 28px rgba(0,0,0,0.5)',
          }}
        >
          {(options || []).length === 0 ? (
            <div style={{ padding: 10, color: '#64748B', fontSize: 12 }}>No options</div>
          ) : (options || []).map(o => {
            const checked = selectedIds.includes(o.value);
            return (
              <label
                key={o.value}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 9px', borderRadius: 4, cursor: 'pointer',
                  fontSize: 12.5, color: '#E5E7EB',
                  background: checked ? 'rgba(59,141,248,0.12)' : 'transparent',
                }}
              >
                <input type="checkbox" checked={checked} onChange={() => toggle(o.value)} style={{ accentColor: '#60A5FA' }} />
                {o.label}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── Small toolbar dropdown used for Columns / Export menus ─── */
function ToolbarMenu({ label, children, align = 'right' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    function onClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button className={`cs-mode-btn${open ? ' active' : ''}`} onClick={() => setOpen(o => !o)} type="button">
        {label} <span style={{ fontSize: 9, marginLeft: 4, opacity: 0.7 }}>▾</span>
      </button>
      {open && (
        <div
          onClick={e => e.stopPropagation()}
          style={{
            position: 'absolute', top: 'calc(100% + 6px)',
            [align]: 0, zIndex: 1000, minWidth: 180,
            background: 'rgba(15,23,42,0.98)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 8, padding: 6,
            boxShadow: '0 12px 28px rgba(0,0,0,0.5)',
            display: 'flex', flexDirection: 'column', gap: 2,
          }}
        >
          {typeof children === 'function' ? children({ close: () => setOpen(false) }) : children}
        </div>
      )}
    </div>
  );
}

function MenuCheck({ checked, onChange, children, title }) {
  return (
    <label
      title={title}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '7px 10px', borderRadius: 5,
        fontSize: 12.5, color: '#E5E7EB', cursor: 'pointer',
        background: checked ? 'rgba(59,141,248,0.12)' : 'transparent',
      }}
      onMouseEnter={e => { if (!checked) e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
      onMouseLeave={e => { if (!checked) e.currentTarget.style.background = 'transparent'; }}
    >
      <input type="checkbox" checked={checked} onChange={onChange} style={{ accentColor: '#60A5FA' }} />
      {children}
    </label>
  );
}

function MenuItem({ onClick, children, title }) {
  return (
    <button
      onClick={onClick} type="button" title={title}
      style={{
        all: 'unset', cursor: 'pointer',
        padding: '7px 10px', borderRadius: 5,
        fontSize: 12.5, color: '#E5E7EB',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      {children}
    </button>
  );
}

/* ─── Inline editable cell ─── */
function EditCell({ value, onChange, type = 'text', options, width, align, min, step, readOnly, className }) {
  if (readOnly) {
    // When the cell has options (select), render the matching option's LABEL
    // instead of the raw value — otherwise locked cost sheets show raw UUIDs
    // for Product / Skill Group / etc. Fall back to the value for free-text cells.
    let display = value;
    if (options && value !== undefined && value !== null && value !== '') {
      const match = options.find(o => (typeof o === 'object' ? o.value : o) === value);
      if (match) display = typeof match === 'object' ? match.label : match;
    }
    if (display === '' || display === null || display === undefined) display = '—';
    return <span className={`cs-readonly ${className || ''}`} style={{ width, textAlign: align || 'right' }}>{display}</span>;
  }
  if (options) {
    return (
      <select className="cs-input" value={value || ''} onChange={e => onChange(e.target.value)} style={{ width: width || 120 }}>
        {options.map(o => {
          const optVal = typeof o === 'object' ? o.value : o;
          const optLabel = typeof o === 'object' ? o.label : o;
          return <option key={optVal} value={optVal}>{optLabel}</option>;
        })}
      </select>
    );
  }
  return (
    <input
      className={`cs-input ${className || ''}`}
      type={type} value={value ?? ''} onChange={e => onChange(type === 'number' ? e.target.value : e.target.value)}
      style={{ width: width || 70, textAlign: align || (type === 'number' ? 'right' : 'left') }}
      min={min} step={step}
    />
  );
}

/* ─── Employee Multi-Select Dropdown ─── */
function EmployeeMultiSelect({ selectedIds, onChange, employees, maxCount, disabled }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);       // root for outside-click detection
  const triggerRef = useRef(null); // trigger for position calculation
  const dropRef = useRef(null);   // the portaled dropdown itself
  // Fixed-position coordinates relative to the viewport. Recomputed when the
  // dropdown opens and on resize/scroll so it tracks the trigger. We use
  // `position: fixed` + a React portal so the dropdown escapes the table's
  // `overflow-x:auto` wrapper and sits above the sticky header (z-index:2).
  const [pos, setPos] = useState({ left: 0, top: 0, width: 260, openUp: true });

  // Close on outside click — check both the root and the portaled dropdown
  useEffect(() => {
    function handleClick(e) {
      if (ref.current && ref.current.contains(e.target)) return;
      if (dropRef.current && dropRef.current.contains(e.target)) return;
      setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Recompute dropdown coordinates while open
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    function reposition() {
      const rect = triggerRef.current.getBoundingClientRect();
      const dropH = 300; // approx dropdown height; we flip up if not enough room below
      const vh = window.innerHeight;
      const openUp = (rect.bottom + dropH > vh) && (rect.top > dropH);
      setPos({
        left: rect.left,
        top: openUp ? (rect.top - 4) : (rect.bottom + 4),
        width: Math.max(rect.width, 280),
        openUp,
      });
    }
    reposition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  const filtered = employees.filter(emp => {
    if (!search) return true;
    const s = search.toLowerCase();
    return emp.name.toLowerCase().includes(s) || (emp.empCode || '').toLowerCase().includes(s);
  });

  function toggleEmployee(empId) {
    if (disabled) return;
    if (selectedIds.includes(empId)) {
      onChange(selectedIds.filter(id => id !== empId));
    } else {
      if (maxCount && selectedIds.length >= maxCount) return;
      onChange([...selectedIds, empId]);
    }
  }

  const selectedNames = selectedIds
    .map(id => employees.find(e => e.id === id))
    .filter(Boolean)
    .map(e => e.name.split(' ')[0])
    .join(', ');

  const statusColor = { green: '#34D399', amber: '#FBBF24', red: '#F87171' };

  const dropdown = (open && !disabled) ? (
    <div
      ref={dropRef}
      className="cs-emp-dropdown cs-emp-dropdown--portal"
      style={{
        position: 'fixed',
        left: pos.left,
        // openUp → anchor by `bottom`, measured from viewport bottom.
        // openDown → anchor by `top`.
        ...(pos.openUp
          ? { bottom: window.innerHeight - pos.top, top: 'auto' }
          : { top: pos.top }),
        width: pos.width,
        zIndex: 10000,
      }}
    >
      <input
        className="cs-input cs-emp-search"
        placeholder="Search employees..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        autoFocus
      />
      <div className="cs-emp-list">
        {filtered.length === 0 && <div className="cs-emp-empty">No employees found</div>}
        {filtered.map(emp => {
          const isSelected = selectedIds.includes(emp.id);
          const atMax = maxCount && selectedIds.length >= maxCount && !isSelected;
          return (
            <div
              key={emp.id}
              className={`cs-emp-option${isSelected ? ' selected' : ''}${atMax ? ' disabled' : ''}`}
              onClick={() => !atMax && toggleEmployee(emp.id)}
            >
              <span className="cs-emp-dot" style={{ background: statusColor[emp.availabilityStatus] }} />
              <span className="cs-emp-name" style={{ color: statusColor[emp.availabilityStatus] }}>
                {emp.name}
              </span>
              <span className="cs-emp-meta">
                {emp.empCode} {emp.designation ? `· ${emp.designation}` : ''}
                {emp.totalAllocPct > 0 ? ` · ${emp.totalAllocPct}%` : ''}
              </span>
              {isSelected && <span className="cs-emp-check">✓</span>}
            </div>
          );
        })}
      </div>
    </div>
  ) : null;

  return (
    <div className="cs-emp-select" ref={ref}>
      <div
        ref={triggerRef}
        className={`cs-emp-trigger${disabled ? ' disabled' : ''}`}
        onClick={() => !disabled && setOpen(!open)}
        title={selectedIds.length ? selectedNames : 'Select employees'}
      >
        {selectedIds.length > 0 ? (
          <span className="cs-emp-pills">
            {selectedIds.map(id => {
              const emp = employees.find(e => e.id === id);
              if (!emp) return null;
              return (
                <span key={id} className="cs-emp-pill" style={{ borderColor: statusColor[emp.availabilityStatus] || '#64748B' }}>
                  <span className="cs-emp-dot" style={{ background: statusColor[emp.availabilityStatus] }} />
                  {emp.name.split(' ')[0]}
                  {!disabled && <span className="cs-emp-pill-x" onClick={e => { e.stopPropagation(); toggleEmployee(id); }}>×</span>}
                </span>
              );
            })}
          </span>
        ) : (
          <span className="cs-emp-placeholder">Select...</span>
        )}
      </div>
      {dropdown && ReactDOM.createPortal(dropdown, document.body)}
    </div>
  );
}


/* ════════════════════════════════════════════════════════════════════════ */
/*  MAIN COMPONENT                                                        */
/* ════════════════════════════════════════════════════════════════════════ */
export default function CostSheet({ estimateId, estimateName, effortLineId, consolidated }) {
  const [settings, setSettings] = useState({ ...DEFAULT_SETTINGS });
  const [resources, setResources] = useState([emptyResource(3)]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [lockStatus, setLockStatus] = useState('draft'); // 'draft' | 'locked'

  // Month column visibility: 'mixed' | 'offshore' | 'onsite' | 'hidden'
  const [allocMode, setAllocMode] = useState('mixed');
  // Collapsible detail columns: Product, Price Type, Source, FTE
  const [showDetailCols, setShowDetailCols] = useState(true);
  // Cost columns toggle
  const [showCostCols, setShowCostCols] = useState(false);
  // Product section & Default Extras visibility
  const [showProductSection, setShowProductSection] = useState(true);
  const [showExtrasDefaults, setShowExtrasDefaults] = useState(true);
  // Accordion collapse state — BOTH start CLOSED by default to keep the
  // header clean. User expands when they need to tweak defaults.
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [extrasOpen,   setExtrasOpen]   = useState(false);
  // Per-column visibility in the resource grid — user toggles these from the
  // Columns ▾ dropdown. All default ON. Rate/Amount/Hours cover both the
  // offshore and onsite variants; which subset actually renders still depends
  // on the Mix/Off/On/Hide allocMode segmented control.
  const [colVis, setColVis] = useState({
    periods: true, hours: true, travels: true,
    rate: true, amount: true,
    totalAmt: true, extras: true, grandTotal: true,
  });
  const toggleCol = (k) => setColVis(v => ({ ...v, [k]: !v[k] }));

  // System-level column Variants — named presets of view prefs. One variant
  // can be flagged as the tenant default (applied to newly-created cost
  // sheets). Estimate-scoped variants were retired — the cost sheet itself
  // now persists its own view prefs in cost_sheet_data.viewPrefs.
  const [variants, setVariants] = useState([]);
  const loadVariants = useCallback(async () => {
    try {
      const r = await apiFetch('/api/cost-sheet-variants');
      if (r.ok) { const d = await r.json(); setVariants(d.variants || []); }
    } catch (_) { /* non-critical */ }
  }, []);
  useEffect(() => { loadVariants(); }, [loadVariants]);

  function applyVariant(v) {
    const cfg = (v && v.config) || {};
    if (cfg.colVis) setColVis(cv => ({ ...cv, ...cfg.colVis }));
    if (typeof cfg.showDetailCols === 'boolean')     setShowDetailCols(cfg.showDetailCols);
    if (typeof cfg.showCostCols === 'boolean')       setShowCostCols(cfg.showCostCols);
    if (typeof cfg.showExtrasDefaults === 'boolean') setShowExtrasDefaults(cfg.showExtrasDefaults);
    if (cfg.allocMode) setAllocMode(cfg.allocMode);
  }

  async function saveVariant() {
    const label = (window.prompt(
      'Save the current column selection as a SYSTEM-wide variant (visible on every cost sheet in this tenant).\nName:'
    ) || '').trim();
    if (!label) return;
    const body = {
      name: label,
      config: { colVis, showDetailCols, showCostCols, showExtrasDefaults, allocMode },
    };
    try {
      const r = await apiFetch('/api/cost-sheet-variants', {
        method: 'POST', body: JSON.stringify(body),
      });
      if (r.ok) { loadVariants(); }
      else { const d = await r.json().catch(() => ({})); window.alert(d.error || 'Failed to save variant'); }
    } catch (e) { window.alert('Network error: ' + e.message); }
  }

  async function setVariantDefault(id) {
    try {
      const r = await apiFetch(`/api/cost-sheet-variants/${id}/default`, { method: 'PATCH' });
      if (r.ok) loadVariants();
    } catch (_) { /* ignore */ }
  }

  async function deleteVariant(id) {
    if (!window.confirm('Delete this variant?')) return;
    try {
      const r = await apiFetch(`/api/cost-sheet-variants/${id}`, { method: 'DELETE' });
      if (r.ok) loadVariants();
    } catch (e) { /* ignore */ }
  }

  // Meta Masters (products & modules)
  const [products, setProducts] = useState([]);
  const [modules, setModules] = useState([]);
  const [moduleProductMap, setModuleProductMap] = useState([]);
  // Canonical consulting-role master (metadata_lookups category='role').
  // Same hook used by Estimates / Contracts / anywhere else that offers a
  // role dropdown, so adding/editing a role in one place updates all of them.
  const { roles: roleOptions } = useRoleOptions();

  // Employee master for cost sheet
  const [allEmployees, setAllEmployees] = useState([]);

  // Versions
  const [versions, setVersions] = useState([]);
  const [versionLabel, setVersionLabel] = useState('');
  const [versionNotes, setVersionNotes] = useState('');
  const [showVersions, setShowVersions] = useState(false);
  const [viewingVersion, setViewingVersion] = useState(null);

  // Comparison
  const [compareMode, setCompareMode] = useState(false);
  const [compareIds, setCompareIds] = useState([]);
  const [compareData, setCompareData] = useState([]);

  // Extras expansion
  const [expandedExtras, setExpandedExtras] = useState({});

  // Exchange rate cache
  const [rateCache, setRateCache] = useState({ 'USD→INR': 94 });
  const [rateWarning, setRateWarning] = useState('');

  /* ─── Load Meta Masters ─── */
  const loadMetaMasters = useCallback(async () => {
    try {
      // /api/metadata/cascade already filters status='Active' server-side and
      // does NOT include the `status` column in the projection. A second
      // client-side `.status === 'active'` filter was silently dropping every
      // row — the Product / Skill Group dropdowns ended up empty.
      const res = await apiFetch('/api/metadata/cascade');
      if (res.ok) {
        const d = await res.json();
        setProducts(d.products || []);
        setModules(d.modules || []);
        setModuleProductMap(d.moduleProducts || []);
      }
    } catch (e) { /* ignore */ }
  }, []);

  /* ─── Load employees with availability ─── */
  const loadEmployees = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/estimates/effort-estimates/${estimateId}/cost-sheet-employees`);
      if (res.ok) {
        const d = await res.json();
        setAllEmployees((d.employees || []).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      }
    } catch (e) { /* ignore */ }
  }, [estimateId]);

  /* ─── Load ─── */
  const loadCostSheet = useCallback(async () => {
    try {
      let url;
      if (effortLineId) url = `/api/estimates/effort-estimates/${estimateId}/lines/${effortLineId}/cost-sheet`;
      else if (consolidated) url = `/api/estimates/effort-estimates/${estimateId}/consolidated-cost-sheet`;
      else url = `/api/estimates/effort-estimates/${estimateId}/cost-sheet`;
      const res = await apiFetch(url);
      let hasSavedData = false;
      if (res.ok) {
        const data = await res.json();
        hasSavedData = !!(data && data.costSheet && data.costSheet.settings);
        if (hasSavedData) {
          const s = { ...DEFAULT_SETTINGS, ...data.costSheet.settings };
          // Migrate old single defaultRate to split rates
          if (s.defaultRate != null && s.defaultOffshoreRate == null) {
            s.defaultOffshoreRate = s.defaultRate;
            s.defaultOnsiteRate = s.defaultRate;
          }
          if (s.billingCurrency === s.displayCurrency) s.exchangeRate = 1;
          setSettings(s);
          if (s.billingCurrency !== s.displayCurrency && s.exchangeRate && s.exchangeRate !== 1) {
            setRateCache(c => ({ ...c, [`${s.billingCurrency}→${s.displayCurrency}`]: s.exchangeRate }));
          }
          if (data.costSheet.rateCache) setRateCache(c => ({ ...c, ...data.costSheet.rateCache }));
          if (data.costSheet.lockStatus) setLockStatus(data.costSheet.lockStatus);
          // For consolidated view, force locked status to prevent saves
          if (consolidated) setLockStatus('locked');
          // Restore saved column-visibility state so the sheet opens exactly
          // the way the user last saved it (draft or locked).
          if (data.costSheet.viewPrefs) {
            const vp = data.costSheet.viewPrefs;
            if (vp.colVis && typeof vp.colVis === 'object') {
              setColVis(cv => ({ ...cv, ...vp.colVis }));
            }
            if (typeof vp.showDetailCols === 'boolean')     setShowDetailCols(vp.showDetailCols);
            if (typeof vp.showCostCols === 'boolean')       setShowCostCols(vp.showCostCols);
            if (typeof vp.showExtrasDefaults === 'boolean') setShowExtrasDefaults(vp.showExtrasDefaults);
            if (vp.allocMode) setAllocMode(vp.allocMode);
          }
          const pc = getPeriodCount(s);
          setResources((data.costSheet.resources || []).map(r => {
            while (r.offshoreMonths.length < pc) r.offshoreMonths.push(0);
            while (r.onsiteMonths.length < pc) r.onsiteMonths.push(0);
            if (!r.employeeIds) r.employeeIds = [];
            if (!r.level) r.level = '';
            // Migrate old single-rate data to split rates
            if (r.rate != null && r.offshoreRate == null) {
              r.offshoreRate = r.rate;
              r.onsiteRate = r.rate;
            }
            if (r.offshoreRate == null) r.offshoreRate = s.defaultOffshoreRate || 30;
            if (r.onsiteRate == null) r.onsiteRate = s.defaultOnsiteRate || 45;
            return r;
          }));
        }
      }
      // New cost sheet (no saved data yet) — apply the tenant's default
      // system variant as the starting view, if one is configured.
      if (!hasSavedData && !consolidated) {
        try {
          const defRes = await apiFetch('/api/cost-sheet-variants/default');
          if (defRes.ok) {
            const defData = await defRes.json();
            const cfg = defData && defData.variant && defData.variant.config;
            if (cfg) {
              if (cfg.colVis && typeof cfg.colVis === 'object') setColVis(cv => ({ ...cv, ...cfg.colVis }));
              if (typeof cfg.showDetailCols === 'boolean')     setShowDetailCols(cfg.showDetailCols);
              if (typeof cfg.showCostCols === 'boolean')       setShowCostCols(cfg.showCostCols);
              if (typeof cfg.showExtrasDefaults === 'boolean') setShowExtrasDefaults(cfg.showExtrasDefaults);
              if (cfg.allocMode) setAllocMode(cfg.allocMode);
            }
          }
        } catch (_) { /* no default — use hardcoded defaults */ }
      }
    } catch (e) { /* first load, no data yet */ }
    setLoading(false);
  }, [estimateId, effortLineId, consolidated]);

  const loadVersions = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/estimates/effort-estimates/${estimateId}/cost-sheet-versions`);
      if (res.ok) { const d = await res.json(); setVersions(d.versions || []); }
    } catch (e) { /* ignore */ }
  }, [estimateId]);

  useEffect(() => { loadCostSheet(); loadVersions(); loadMetaMasters(); loadEmployees(); }, [loadCostSheet, loadVersions, loadMetaMasters, loadEmployees]);

  /* ─── Backward-compat helpers: turn legacy single-value fields into arrays ─── */
  function asArr(v) {
    if (Array.isArray(v)) return v.filter(Boolean);
    if (v) return [v];
    return [];
  }
  /* settings.productId may be legacy single; settings.productIds is the new array */
  const settingsProductIds = asArr(settings.productIds && settings.productIds.length ? settings.productIds : settings.productId);

  /** Return the modules linked to ANY of the given product ids (union). If the
   *  set is empty, return all modules. */
  function getModulesForProducts(productIds) {
    const ids = asArr(productIds);
    if (ids.length === 0) return modules;
    const linked = new Set(
      moduleProductMap.filter(mp => ids.includes(mp.product_id)).map(mp => mp.module_id)
    );
    return modules.filter(m => linked.has(m.id) || ids.includes(m.product_id));
  }

  /* ─── Product/module option builders (plain, no "— None —" — multi-select has its own UI) ─── */
  const productOptions      = products.map(p => ({ value: p.id, label: p.name }));
  const productOptionsSingle = [{ value: '', label: '— None —' }, ...productOptions];
  function moduleOptionsForLine(lineProductIds) {
    const pids = asArr(lineProductIds).length ? asArr(lineProductIds) : settingsProductIds;
    const mods = getModulesForProducts(pids);
    return mods.map(m => ({ value: m.id, label: m.name }));
  }
  function productName(id) { const p = products.find(x => x.id === id); return p ? p.name : ''; }
  function moduleName(id) { const m = modules.find(x => x.id === id); return m ? m.name : ''; }

  /* ─── Helper: build cache key from two currencies ─── */
  function rateKey(from, to) { return `${from}→${to}`; }

  /* ─── Resolve exchange rate for a currency pair ─── */
  function resolveRate(billing, display) {
    if (billing === display) return 1;
    const key = rateKey(billing, display);
    if (rateCache[key] != null) return rateCache[key];
    const rev = rateKey(display, billing);
    if (rateCache[rev] != null && rateCache[rev] !== 0) return Math.round(1 / rateCache[rev] * 10000) / 10000;
    return null;
  }

  /* ─── Employee helpers ─── */
  // Filter employees by module and level for a specific line
  function getEmployeesForLine(res) {
    let filtered = allEmployees;
    // Filter by module (line-level module(s) — legacy single or new array)
    const modIds = asArr(res.moduleIds && res.moduleIds.length ? res.moduleIds : res.moduleId);
    if (modIds.length > 0) {
      const modNames = modIds.map(id => (modules.find(m => m.id === id) || {}).name).filter(Boolean);
      filtered = filtered.filter(e => {
        if (!e.module) return true;
        return modNames.includes(e.module) || modIds.includes(e.module);
      });
    }
    // Filter by level
    if (res.level) {
      const levelNum = parseInt(res.level.replace('L', ''), 10);
      if (!isNaN(levelNum)) {
        filtered = filtered.filter(e => !e.level || e.level === levelNum);
      }
    }
    return filtered;
  }

  // Calculate line cost from assigned employees
  function calcLineCost(res) {
    if (!res.employeeIds || res.employeeIds.length === 0) return 0;
    const totalMonths = res.offshoreMonths.reduce((a, v) => a + (parseFloat(v) || 0), 0)
                      + res.onsiteMonths.reduce((a, v) => a + (parseFloat(v) || 0), 0);
    let totalCost = 0;
    for (const empId of res.employeeIds) {
      const emp = allEmployees.find(e => e.id === empId);
      if (emp && emp.totalCtc) {
        // Monthly cost = annual CTC / 12, proportioned by FTE allocation per employee
        const monthlyCost = emp.totalCtc / 12;
        const empFte = (parseFloat(res.fte) || 1) / res.employeeIds.length;
        totalCost += monthlyCost * empFte * totalMonths;
      }
    }
    return totalCost;
  }

  /* ─── Settings changes ─── */
  function updateSetting(key, val) {
    setSettings(prev => {
      const next = { ...prev, [key]: val };

      if (key === 'months') {
        const m = parseInt(val, 10) || 3;
        const pc = (next.effortUnit === 'week') ? m * 4 : m;
        setResources(prev2 => prev2.map(r => {
          const off = [...r.offshoreMonths]; const ons = [...r.onsiteMonths];
          while (off.length < pc) off.push(0);
          while (ons.length < pc) ons.push(0);
          return { ...r, offshoreMonths: off.slice(0, pc), onsiteMonths: ons.slice(0, pc), duration: m };
        }));
      }

      if (key === 'effortUnit') {
        const oldUnit = prev.effortUnit || 'month';
        const m = prev.months || 3;
        if (val !== oldUnit) {
          setResources(prev2 => prev2.map(r => {
            if (val === 'week') {
              // Expand: each month → 4 weeks with same FTE
              const newOff = []; const newOns = [];
              const len = Math.min(r.offshoreMonths.length, m); // use month count
              for (let i = 0; i < len; i++) {
                for (let w = 0; w < 4; w++) { newOff.push(r.offshoreMonths[i]); newOns.push(r.onsiteMonths[i]); }
              }
              return { ...r, offshoreMonths: newOff, onsiteMonths: newOns };
            } else {
              // Compress: every 4 weeks → 1 month (average)
              const newOff = []; const newOns = [];
              for (let i = 0; i < m; i++) {
                const offSlice = r.offshoreMonths.slice(i * 4, (i + 1) * 4);
                const onsSlice = r.onsiteMonths.slice(i * 4, (i + 1) * 4);
                newOff.push(offSlice.length > 0 ? Math.round(offSlice.reduce((a, v) => a + (parseFloat(v) || 0), 0) / offSlice.length * 4) / 4 : 0);
                newOns.push(onsSlice.length > 0 ? Math.round(onsSlice.reduce((a, v) => a + (parseFloat(v) || 0), 0) / onsSlice.length * 4) / 4 : 0);
              }
              return { ...r, offshoreMonths: newOff, onsiteMonths: newOns };
            }
          }));
        }
      }

      if (key === 'billingCurrency' || key === 'displayCurrency') {
        const billing = key === 'billingCurrency' ? val : prev.billingCurrency;
        const display = key === 'displayCurrency' ? val : prev.displayCurrency;

        if (billing === display) {
          next.exchangeRate = 1;
          setRateWarning('');
        } else {
          const cached = resolveRate(billing, display);
          if (cached != null) {
            next.exchangeRate = cached;
            setRateWarning('');
          } else {
            setRateWarning(`No saved rate for ${billing} → ${display}. Please set the exchange rate.`);
          }
        }
      }

      if (key === 'exchangeRate') {
        const billing = prev.billingCurrency;
        const display = prev.displayCurrency;
        if (billing !== display) {
          const numVal = parseFloat(val) || 0;
          setRateCache(c => ({ ...c, [rateKey(billing, display)]: numVal }));
          setRateWarning('');
        }
      }

      return next;
    });
    setDirty(true);
  }

  /* ─── Resource CRUD ─── */
  function addResource() {
    const pc = getPeriodCount(settings);
    setResources(prev => [...prev, { ...emptyResource(pc, settings), productIds: settingsProductIds.slice() }]);
    setDirty(true);
  }
  function removeResource(idx) {
    setResources(prev => prev.filter((_, i) => i !== idx));
    setDirty(true);
  }
  /** Copy the header-level Offshore & Onsite rates onto every existing line.
   *  (New rows already pick up these defaults via emptyResource(), so no
   *   persistent config change is needed.) */
  function applyDefaultRatesToAll() {
    setResources(prev => prev.map(r => ({
      ...r,
      offshoreRate: settings.defaultOffshoreRate,
      onsiteRate:   settings.defaultOnsiteRate,
    })));
    setDirty(true);
  }
  /** Duplicate a single resource row (inserted right after the source). */
  function duplicateResource(idx) {
    setResources(prev => {
      if (idx < 0 || idx >= prev.length) return prev;
      const copy = JSON.parse(JSON.stringify(prev[idx]));
      return [...prev.slice(0, idx + 1), copy, ...prev.slice(idx + 1)];
    });
    setDirty(true);
  }
  /** Spread each line's FTE across every period (M1..Mn) based on allocMode:
   *  - Off  → offshoreMonths[i] = fte, onsite = 0
   *  - On   → onsiteMonths[i]   = fte, offshore = 0
   *  - Mix  → if a line already has allocations, preserve the off/onsite ratio
   *           per period; otherwise default to full onsite.
   */
  function spreadEffort() {
    const pc = getPeriodCount(settings);
    setResources(prev => prev.map(r => {
      const fte = parseFloat(r.fte) || 0;
      const off = Array.from({ length: pc }, (_, i) => parseFloat((r.offshoreMonths || [])[i]) || 0);
      const ons = Array.from({ length: pc }, (_, i) => parseFloat((r.onsiteMonths   || [])[i]) || 0);
      for (let i = 0; i < pc; i++) {
        if (allocMode === 'offshore') { off[i] = fte; ons[i] = 0; }
        else if (allocMode === 'onsite') { ons[i] = fte; off[i] = 0; }
        else {
          const sum = off[i] + ons[i];
          if (sum > 0) {
            const ratio = off[i] / sum;
            off[i] = Math.round(fte * ratio * 100) / 100;
            ons[i] = Math.round(fte * (1 - ratio) * 100) / 100;
          } else {
            off[i] = 0; ons[i] = fte; // sensible default
          }
        }
      }
      return { ...r, offshoreMonths: off, onsiteMonths: ons };
    }));
    setDirty(true);
  }
  function updateResource(idx, key, val) {
    setResources(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      const updated = { ...r, [key]: val };
      if (key === 'fte') {
        const newFte = parseFloat(val) || 0;
        const off = [...updated.offshoreMonths];
        const ons = [...updated.onsiteMonths];
        for (let mi = 0; mi < off.length; mi++) {
          const totalM = (parseFloat(off[mi]) || 0) + (parseFloat(ons[mi]) || 0);
          if (totalM > newFte) {
            if (totalM > 0) {
              off[mi] = Math.round((parseFloat(off[mi]) || 0) / totalM * newFte * 4) / 4;
              ons[mi] = Math.round((newFte - off[mi]) * 4) / 4;
            } else {
              off[mi] = newFte; ons[mi] = 0;
            }
          }
        }
        updated.offshoreMonths = off;
        updated.onsiteMonths = ons;
      }
      if (key === 'productId') {
        const validMods = getModulesForProduct(val);
        if (updated.moduleId && !validMods.some(m => m.id === updated.moduleId)) {
          updated.moduleId = '';
        }
      }
      return updated;
    }));
    setDirty(true);
  }

  /* ─── Month/week allocation with FTE enforcement (partial first period) ─── */
  function updateMonthAlloc(idx, field, mIdx, rawVal) {
    setResources(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      const fte = parseFloat(r.fte) || 0;
      const maxForPeriod = getMaxFteForPeriod(mIdx, fte, settings);
      let val = parseFloat(rawVal) || 0;
      if (val < 0) val = 0;
      if (val > maxForPeriod) val = maxForPeriod;

      const offArr = [...r.offshoreMonths];
      const onsArr = [...r.onsiteMonths];

      if (field === 'onsiteMonths') {
        onsArr[mIdx] = val;
        offArr[mIdx] = Math.max(0, Math.round((maxForPeriod - val) * 4) / 4);
      } else {
        offArr[mIdx] = val;
        onsArr[mIdx] = Math.max(0, Math.round((maxForPeriod - val) * 4) / 4);
      }

      return { ...r, offshoreMonths: offArr, onsiteMonths: onsArr };
    }));
    setDirty(true);
  }

  /* ─── Totals ─── */
  const includedResources = resources.filter(r => r.priceType === 'Included');
  const totalEffort = includedResources.reduce((a, r) => a + calcAmount(r, settings), 0);
  const totalExtras = includedResources.reduce((a, r) => a + calcExtras(r, settings), 0);
  const grandTotal = totalEffort + totalExtras;
  const discount = grandTotal * -(settings.discountPct / 100);
  const afterDiscount = grandTotal + discount;
  const proposedValue = Math.round(afterDiscount / 1000) * 1000;
  const displayTotal = proposedValue * settings.exchangeRate;

  // Cost totals
  const totalLineCost = resources.reduce((a, r) => a + calcLineCost(r), 0);
  const totalOverhead = totalLineCost * ((settings.overheadPct || 0) / 100);
  const totalCostAll = totalLineCost + totalOverhead;
  // Margin = Total Price (effort only, no extras) - Total Cost
  const totalMargin = totalEffort - totalCostAll;

  /* ─── Save ─── */
  async function handleSave(mode) {
    if (consolidated) return; // consolidated view is read-only
    setSaving(true);
    const saveMode = mode || 'draft';
    // View preferences are saved alongside settings + resources so the cost
    // sheet reopens with the exact columns / mode the user was using.
    const viewPrefs = { colVis, showDetailCols, showCostCols, showExtrasDefaults, allocMode };
    const payload = { settings, resources, rateCache, lockStatus: saveMode, viewPrefs };
    let saveUrl;
    if (effortLineId) saveUrl = `/api/estimates/effort-estimates/${estimateId}/lines/${effortLineId}/cost-sheet`;
    else saveUrl = `/api/estimates/effort-estimates/${estimateId}/cost-sheet`;
    try {
      await apiFetch(saveUrl, {
        method: 'PUT', body: JSON.stringify({ costSheet: payload })
      });
      setLockStatus(saveMode);
      setDirty(false);

      // If locking, create soft allocations
      if (saveMode === 'locked') {
        await apiFetch(`/api/estimates/effort-estimates/${estimateId}/cost-sheet-lock`, { method: 'POST' });
        loadEmployees(); // refresh availability
      }
    } catch (e) { /* ignore */ }
    setSaving(false);
  }

  async function handleUnlock() {
    if (!window.confirm('Unlock this cost sheet? Soft allocations for assigned employees will be removed.')) return;
    setSaving(true);
    try {
      await apiFetch(`/api/estimates/effort-estimates/${estimateId}/cost-sheet-unlock`, { method: 'POST' });
      setLockStatus('draft');
      loadEmployees();
    } catch (e) { /* ignore */ }
    setSaving(false);
  }

  /* ─── Excel Export ─── */
  function exportToExcel(mode) {
    // mode: 'costSheet' (full) or 'pricingSheet' (effort + pricing only)
    const isCost = mode === 'costSheet';
    const sc = settings.rateScale || '1';
    const pc = getPeriodCount(settings);
    const sym = getCurrencySymbol(settings.billingCurrency);
    const rateUnit = (settings.ratePer || 'ManHour').replace('Man', '').toLowerCase();

    // Build header row
    const headers = ['#', 'Role', 'Level'];
    if (isCost) headers.push('Employee');
    headers.push('Skill Group', 'Product', 'Price Type', 'Source', 'FTE');
    for (let i = 0; i < pc; i++) headers.push(`Off ${getPeriodLabel(i, settings)}`);
    for (let i = 0; i < pc; i++) headers.push(`On ${getPeriodLabel(i, settings)}`);
    headers.push('Off Hrs', 'On Hrs', 'Travels');
    headers.push(`Off Rate (/${rateUnit})`, 'Off Amt', `On Rate (/${rateUnit})`, 'On Amt');
    headers.push('Total Amt', 'Extras', 'Total');
    if (isCost) headers.push('Employee Cost', 'Overhead', 'Total Cost', 'Margin');

    // Build data rows
    const rows = resources.map((res, idx) => {
      const offH = calcOffshoreHrs(res, settings);
      const onsH = calcOnsiteHrs(res, settings);
      const amt = calcAmount(res, settings);
      const ext = calcExtras(res, settings);
      const total = amt + ext;

      const row = [idx + 1, res.role, res.level || ''];
      if (isCost) {
        const empNames = (res.employeeIds || []).map(id => {
          const e = allEmployees.find(emp => emp.id === id);
          return e ? e.name : '';
        }).filter(Boolean).join(', ');
        row.push(empNames);
      }
      row.push(
        moduleName(res.moduleId) || '',
        productName(res.productId) || '',
        res.priceType, res.source, res.fte
      );
      for (let i = 0; i < pc; i++) row.push(res.offshoreMonths[i] || 0);
      for (let i = 0; i < pc; i++) row.push(res.onsiteMonths[i] || 0);
      row.push(offH, onsH, res.returnTravel || 0);
      row.push(
        toDisplay(res.offshoreRate, sc),
        toDisplay(calcOffshoreAmt(res, settings), sc),
        toDisplay(res.onsiteRate, sc),
        toDisplay(calcOnsiteAmt(res, settings), sc)
      );
      row.push(toDisplay(amt, sc), toDisplay(ext, sc), toDisplay(total, sc));
      if (isCost) {
        const lineCost = calcLineCost(res);
        const lineOverhead = lineCost * ((settings.overheadPct || 0) / 100);
        const lineTotalCost = lineCost + lineOverhead;
        const lineMargin = amt - lineTotalCost;
        row.push(toDisplay(lineCost, sc), toDisplay(lineOverhead, sc), toDisplay(lineTotalCost, sc), toDisplay(lineMargin, sc));
      }
      return row;
    });

    // Summary rows
    const blankRow = [];
    const summaryRows = [
      blankRow,
      ['', 'Total Effort', '', '', '', '', '', '', ...Array(pc * 2).fill(''), '', '', '', '', toDisplay(totalEffort, sc)],
      ['', 'Total Extras', '', '', '', '', '', '', ...Array(pc * 2).fill(''), '', '', '', '', '', toDisplay(totalExtras, sc)],
      ['', 'Grand Total', '', '', '', '', '', '', ...Array(pc * 2).fill(''), '', '', '', '', '', '', toDisplay(grandTotal, sc)],
      ['', `Discount (${settings.discountPct}%)`, '', '', '', '', '', '', ...Array(pc * 2).fill(''), '', '', '', '', '', '', toDisplay(discount, sc)],
      ['', 'Proposed Value', '', '', '', '', '', '', ...Array(pc * 2).fill(''), '', '', '', '', '', '', toDisplay(proposedValue, sc)],
    ];
    if (isCost) {
      summaryRows.push(blankRow);
      summaryRows.push(['', 'Total Employee Cost', '', '', '', '', '', '', ...Array(pc * 2 + 9).fill(''), toDisplay(totalLineCost, sc)]);
      summaryRows.push(['', `Total Overhead (${settings.overheadPct}%)`, '', '', '', '', '', '', ...Array(pc * 2 + 9).fill(''), '', toDisplay(totalOverhead, sc)]);
      summaryRows.push(['', 'Total Cost', '', '', '', '', '', '', ...Array(pc * 2 + 9).fill(''), '', '', toDisplay(totalCostAll, sc)]);
      summaryRows.push(['', 'Margin', '', '', '', '', '', '', ...Array(pc * 2 + 9).fill(''), '', '', '', toDisplay(totalMargin, sc)]);
    }

    // Settings info sheet
    const infoData = [
      ['Estimate', estimateName || estimateId],
      ['Billing Currency', settings.billingCurrency],
      ['Display Currency', settings.displayCurrency],
      ['Exchange Rate', settings.exchangeRate],
      ['Rate Per', settings.ratePer || 'ManHour'],
      ['Rate Scale', scaleLabel(sc) || 'Absolute'],
      ['Effort Unit', settings.effortUnit || 'month'],
      ['Mandays/Month', settings.mandaysPerMonth],
      ['Hours/Day', settings.hoursPerDay],
      ['Duration (Months)', settings.months],
      ['Start Date', settings.startDate || ''],
      ['Discount %', settings.discountPct],
      ['Overhead %', settings.overheadPct],
    ];

    // Extras detail sheet
    const extrasHeaders = ['#', 'Role', 'Level', 'Per Diem', 'Visa', 'Conveyance', 'Lodging', 'Boarding', 'Total Extras'];
    const extrasRows = resources.map((res, idx) => [
      idx + 1, res.role, res.level || '',
      toDisplay(calcPerDiem(res, settings), sc),
      toDisplay(calcVisa(res), sc),
      toDisplay(parseFloat(res.conveyanceRate) || 0, sc),
      toDisplay(calcLodging(res, settings), sc),
      toDisplay(calcBoarding(res, settings), sc),
      toDisplay(calcExtras(res, settings), sc),
    ]);

    // Create workbook
    const wb = XLSX.utils.book_new();
    const wsData = [headers, ...rows, ...summaryRows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, isCost ? 'Cost Sheet' : 'Pricing Sheet');
    const wsExtras = XLSX.utils.aoa_to_sheet([extrasHeaders, ...extrasRows]);
    XLSX.utils.book_append_sheet(wb, wsExtras, 'Extras Detail');
    const wsInfo = XLSX.utils.aoa_to_sheet(infoData);
    XLSX.utils.book_append_sheet(wb, wsInfo, 'Settings');

    // Download
    const fileName = `${(estimateName || 'Estimate').replace(/[^a-zA-Z0-9]/g, '_')}_${isCost ? 'CostSheet' : 'PricingSheet'}.xlsx`;
    XLSX.writeFile(wb, fileName);
  }

  /* ─── Version management ─── */
  async function saveVersion() {
    if (!versionLabel.trim()) return;
    setSaving(true);
    try {
      await apiFetch(`/api/estimates/effort-estimates/${estimateId}/cost-sheet-versions`, {
        method: 'POST',
        body: JSON.stringify({
          versionLabel,
          costSheet: { settings, resources },
          totalAmount: grandTotal,
          proposedValue,
          notes: versionNotes
        })
      });
      setVersionLabel(''); setVersionNotes('');
      loadVersions();
    } catch (e) { /* ignore */ }
    setSaving(false);
  }

  async function viewVersion(vId) {
    try {
      const res = await apiFetch(`/api/estimates/effort-estimates/${estimateId}/cost-sheet-versions/${vId}`);
      if (res.ok) { const d = await res.json(); setViewingVersion(d.version); }
    } catch (e) { /* ignore */ }
  }

  async function restoreVersion(vId) {
    if (!window.confirm('Restore this version? Current cost sheet will be overwritten.')) return;
    try {
      await apiFetch(`/api/estimates/effort-estimates/${estimateId}/cost-sheet-versions/${vId}/restore`, { method: 'POST' });
      loadCostSheet();
    } catch (e) { /* ignore */ }
  }

  async function deleteVersion(vId) {
    if (!window.confirm('Delete this saved version?')) return;
    try {
      await apiFetch(`/api/estimates/effort-estimates/${estimateId}/cost-sheet-versions/${vId}`, { method: 'DELETE' });
      loadVersions();
    } catch (e) { /* ignore */ }
  }

  /* ─── Comparison ─── */
  function toggleCompare(vId) {
    setCompareIds(prev => {
      if (prev.includes(vId)) return prev.filter(x => x !== vId);
      if (prev.length >= 3) return prev;
      return [...prev, vId];
    });
  }

  async function startComparison() {
    if (compareIds.length < 2) return;
    const results = [];
    for (const vId of compareIds) {
      try {
        const res = await apiFetch(`/api/estimates/effort-estimates/${estimateId}/cost-sheet-versions/${vId}`);
        if (res.ok) { const d = await res.json(); results.push(d.version); }
      } catch (e) { /* ignore */ }
    }
    setCompareData(results);
    setCompareMode(true);
  }

  function calcVersionTotals(v) {
    if (!v || !v.cost_sheet_data) return {};
    const s = v.cost_sheet_data.settings || DEFAULT_SETTINGS;
    const rs = (v.cost_sheet_data.resources || []).filter(r => r.priceType === 'Included');
    const effort = rs.reduce((a, r) => a + calcAmount(r, s), 0);
    const extras = rs.reduce((a, r) => a + calcExtras(r, s), 0);
    const gt = effort + extras;
    const disc = gt * -(s.discountPct / 100);
    const pv = Math.round((gt + disc) / 1000) * 1000;
    return { effort, extras, grandTotal: gt, discount: disc, proposedValue: pv, resources: rs, settings: s, allResources: v.cost_sheet_data.resources || [] };
  }

  /* ─── Toggle extras row ─── */
  function toggleExtras(idx) {
    setExpandedExtras(prev => ({ ...prev, [idx]: !prev[idx] }));
  }

  const isLocked = lockStatus === 'locked';

  if (loading) return <div className="cs-loading">Loading cost sheet...</div>;

  /* ════════════════════════════════════════════════════════════════════ */
  /*  COMPARISON VIEW                                                    */
  /* ════════════════════════════════════════════════════════════════════ */
  if (compareMode && compareData.length >= 2) {
    const cols = compareData.map(v => ({ ...v, calc: calcVersionTotals(v) }));
    return (
      <div className="cs-container">
        <div className="cs-toolbar">
          <h4 style={{ margin: 0 }}>Version Comparison</h4>
          <button className="btn btn-secondary" onClick={() => { setCompareMode(false); setCompareData([]); setCompareIds([]); }}>Back to Cost Sheet</button>
        </div>

        <div className="cs-compare-grid" style={{ gridTemplateColumns: `200px repeat(${cols.length}, 1fr)` }}>
          <div className="cs-compare-label cs-compare-header">Metric</div>
          {cols.map((c, i) => <div key={i} className="cs-compare-val cs-compare-header">{c.version_label}</div>)}

          <div className="cs-compare-label">Product</div>
          {cols.map((c, i) => <div key={i} className="cs-compare-val">{productName(c.calc.settings?.productId) || '—'}</div>)}

          <div className="cs-compare-label">Billing Currency</div>
          {cols.map((c, i) => <div key={i} className="cs-compare-val">{c.calc.settings?.billingCurrency || 'USD'}</div>)}

          <div className="cs-compare-label">Exchange Rate</div>
          {cols.map((c, i) => <div key={i} className="cs-compare-val">{c.calc.settings?.exchangeRate || '—'}</div>)}

          <div className="cs-compare-label">Resources</div>
          {cols.map((c, i) => <div key={i} className="cs-compare-val">{c.calc.allResources?.length || 0}</div>)}

          <div className="cs-compare-label">Included Resources</div>
          {cols.map((c, i) => <div key={i} className="cs-compare-val">{c.calc.resources?.length || 0}</div>)}

          <div className="cs-compare-label cs-compare-section">Role Breakdown</div>
          {cols.map((c, i) => <div key={i} className="cs-compare-val cs-compare-section">—</div>)}
          {(() => {
            const allRoles = [...new Set(cols.flatMap(c => (c.calc.allResources || []).map(r => r.role)))];
            return allRoles.map(role => (
              <React.Fragment key={role}>
                <div className="cs-compare-label" style={{ paddingLeft: 24 }}>{role}</div>
                {cols.map((c, i) => {
                  const matching = (c.calc.allResources || []).filter(r => r.role === role);
                  const amt = matching.reduce((a, r) => a + calcAmount(r, c.calc.settings), 0);
                  return <div key={i} className="cs-compare-val">{getCurrencySymbol(c.calc.settings?.billingCurrency)}{fmtNum(amt, 0)}</div>;
                })}
              </React.Fragment>
            ));
          })()}

          <div className="cs-compare-label cs-compare-section">Financials</div>
          {cols.map((c, i) => <div key={i} className="cs-compare-val cs-compare-section">—</div>)}

          <div className="cs-compare-label">Total Effort</div>
          {cols.map((c, i) => <div key={i} className="cs-compare-val">{getCurrencySymbol(c.calc.settings?.billingCurrency)}{fmtNum(c.calc.effort, 0)}</div>)}

          <div className="cs-compare-label">Total Extras</div>
          {cols.map((c, i) => <div key={i} className="cs-compare-val">{getCurrencySymbol(c.calc.settings?.billingCurrency)}{fmtNum(c.calc.extras, 0)}</div>)}

          <div className="cs-compare-label cs-compare-total">Grand Total</div>
          {cols.map((c, i) => <div key={i} className="cs-compare-val cs-compare-total">{getCurrencySymbol(c.calc.settings?.billingCurrency)}{fmtNum(c.calc.grandTotal, 0)}</div>)}

          <div className="cs-compare-label">Discount ({cols[0]?.calc.settings?.discountPct || 10}%)</div>
          {cols.map((c, i) => <div key={i} className="cs-compare-val" style={{ color: '#EF4444' }}>{getCurrencySymbol(c.calc.settings?.billingCurrency)}{fmtNum(c.calc.discount, 0)}</div>)}

          <div className="cs-compare-label cs-compare-total">Proposed Value</div>
          {cols.map((c, i) => <div key={i} className="cs-compare-val cs-compare-total cs-compare-highlight">{getCurrencySymbol(c.calc.settings?.billingCurrency)}{fmtNum(c.calc.proposedValue, 0)}</div>)}

          <div className="cs-compare-label">Notes</div>
          {cols.map((c, i) => <div key={i} className="cs-compare-val" style={{ fontSize: 12 }}>{c.notes || '—'}</div>)}

          <div className="cs-compare-label">Created</div>
          {cols.map((c, i) => <div key={i} className="cs-compare-val">{formatDate(c.created_at)}</div>)}
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════════════════════════════════ */
  /*  VERSION DETAIL VIEW                                                */
  /* ════════════════════════════════════════════════════════════════════ */
  if (viewingVersion) {
    const vData = viewingVersion.cost_sheet_data || {};
    const vSettings = { ...DEFAULT_SETTINGS, ...vData.settings };
    const vResources = vData.resources || [];
    const vIncluded = vResources.filter(r => r.priceType === 'Included');
    const vEffort = vIncluded.reduce((a, r) => a + calcAmount(r, vSettings), 0);
    const vExtras = vIncluded.reduce((a, r) => a + calcExtras(r, vSettings), 0);
    const vGrand = vEffort + vExtras;
    const vDiscount = vGrand * -(vSettings.discountPct / 100);
    const vProposed = Math.round((vGrand + vDiscount) / 1000) * 1000;
    const sym = getCurrencySymbol(vSettings.billingCurrency);

    return (
      <div className="cs-container">
        <div className="cs-toolbar">
          <h4 style={{ margin: 0 }}>Version: {viewingVersion.version_label}</h4>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={() => restoreVersion(viewingVersion.id)}>Restore This Version</button>
            <button className="btn btn-secondary" onClick={() => setViewingVersion(null)}>Back</button>
          </div>
        </div>
        {viewingVersion.notes && <div className="cs-version-note">{viewingVersion.notes}</div>}
        <div className="cs-settings-bar">
          {vSettings.productId && <span>Product: {productName(vSettings.productId) || vSettings.productId}</span>}
          <span>Currency: {vSettings.billingCurrency}</span>
          <span>Display: {vSettings.displayCurrency}</span>
          <span>Exchange: {vSettings.exchangeRate}</span>
          <span>Mandays/Mo: {vSettings.mandaysPerMonth}</span>
          <span>Hrs/Day: {vSettings.hoursPerDay}</span>
        </div>

        <div className="cs-table-wrap">
          <table className="cs-table">
            {(() => {
              const vpc = getPeriodCount(vSettings);
              return (
                <thead>
                  <tr>
                    <th rowSpan={2}>#</th><th rowSpan={2}>Role</th><th rowSpan={2}>Level</th><th rowSpan={2}>Skill Group</th><th rowSpan={2}>Product</th><th rowSpan={2}>Type</th><th rowSpan={2}>Source</th><th rowSpan={2}>FTE</th>
                    <th colSpan={vpc} className="cs-th-offshore-group">Offshore</th>
                    <th colSpan={vpc} className="cs-th-onsite-group">Onsite</th>
                    <th rowSpan={2}>Off Hrs</th><th rowSpan={2}>On Hrs</th>
                    <th rowSpan={2} className="cs-th-offshore">Off Rate</th><th rowSpan={2} className="cs-th-offshore">Off Amt</th>
                    <th rowSpan={2} className="cs-th-onsite">On Rate</th><th rowSpan={2} className="cs-th-onsite">On Amt</th>
                    <th rowSpan={2}>Total Amt</th><th rowSpan={2}>Extras</th><th rowSpan={2}>Total</th>
                  </tr>
                  <tr>
                    {Array.from({ length: vpc }, (_, i) => <th key={`oh${i}`} className="cs-th-offshore" style={{ fontSize: vSettings.showActualPeriod ? 10 : 12 }}>{getPeriodLabel(i, vSettings)}</th>)}
                    {Array.from({ length: vpc }, (_, i) => <th key={`on${i}`} className="cs-th-onsite" style={{ fontSize: vSettings.showActualPeriod ? 10 : 12 }}>{getPeriodLabel(i, vSettings)}</th>)}
                  </tr>
                </thead>
              );
            })()}
            <tbody>
              {vResources.map((r, i) => {
                const offH = calcOffshoreHrs(r, vSettings);
                const onsH = calcOnsiteHrs(r, vSettings);
                const offAmt = calcOffshoreAmt(r, vSettings);
                const onsAmt = calcOnsiteAmt(r, vSettings);
                const amt = offAmt + onsAmt;
                const ext = calcExtras(r, vSettings);
                return (
                  <tr key={i} className={r.priceType === 'Excluded' ? 'cs-row-excluded' : ''}>
                    <td>{i + 1}</td>
                    <td>{r.role}</td>
                    <td>{r.level || '—'}</td>
                    <td style={{ fontSize: 11 }}>{moduleName(r.moduleId) || '—'}</td>
                    <td style={{ fontSize: 11 }}>{productName(r.productId) || '—'}</td>
                    <td><span className={`badge ${r.priceType === 'Included' ? 'badge-green' : 'badge-yellow'}`}>{r.priceType}</span></td>
                    <td>{r.source}</td>
                    <td style={{ textAlign: 'right' }}>{r.fte}</td>
                    {r.offshoreMonths.slice(0, getPeriodCount(vSettings)).map((v, mi) => <td key={`o${mi}`} className="cs-td-offshore" style={{ textAlign: 'right' }}>{v}</td>)}
                    {r.onsiteMonths.slice(0, getPeriodCount(vSettings)).map((v, mi) => <td key={`n${mi}`} className="cs-td-onsite" style={{ textAlign: 'right' }}>{v}</td>)}
                    <td style={{ textAlign: 'right' }}>{fmtNum(offH)}</td>
                    <td style={{ textAlign: 'right' }}>{fmtNum(onsH)}</td>
                    <td style={{ textAlign: 'right' }}>{sym}{fmtNum(r.offshoreRate || r.rate)}</td>
                    <td style={{ textAlign: 'right' }}>{sym}{fmtNum(offAmt, 0)}</td>
                    <td style={{ textAlign: 'right' }}>{sym}{fmtNum(r.onsiteRate || r.rate)}</td>
                    <td style={{ textAlign: 'right' }}>{sym}{fmtNum(onsAmt, 0)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{sym}{fmtNum(amt, 0)}</td>
                    <td style={{ textAlign: 'right' }}>{sym}{fmtNum(ext, 0)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{sym}{fmtNum(amt + ext, 0)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="cs-summary-bar">
          <div className="cs-summary-item"><span>Total Effort</span><strong>{sym}{fmtNum(vEffort, 0)}</strong></div>
          <div className="cs-summary-item"><span>Total Extras</span><strong>{sym}{fmtNum(vExtras, 0)}</strong></div>
          <div className="cs-summary-item"><span>Grand Total</span><strong>{sym}{fmtNum(vGrand, 0)}</strong></div>
          <div className="cs-summary-item"><span>Discount ({vSettings.discountPct}%)</span><strong style={{ color: '#EF4444' }}>{sym}{fmtNum(vDiscount, 0)}</strong></div>
          <div className="cs-summary-item cs-summary-highlight"><span>Proposed Value</span><strong>{sym}{fmtNum(vProposed, 0)}</strong></div>
        </div>
      </div>
    );
  }

  const sym = getCurrencySymbol(settings.billingCurrency);
  const sc = settings.rateScale || '1';
  const scLbl = scaleLabel(sc);  // '', 'K', 'L', 'M', 'Cr'
  const amtLabel = scLbl ? `${scLbl}${sym}` : sym;  // e.g. 'K$' or '$'
  const scDec = sc === '1' ? 0 : 1; // decimal places for scaled display

  /* ════════════════════════════════════════════════════════════════════ */
  /*  MAIN EDITABLE VIEW                                                 */
  /* ════════════════════════════════════════════════════════════════════ */
  return (
    <div className="cs-container">
      {/* ─── Toolbar ─── */}
      <div className="cs-toolbar">
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {/* Compact status chips only — the "Cost Sheet" label is already
              rendered by the tab above, so we omit it here. */}
          {consolidated && (
            <span
              className="badge"
              title="The Consolidated view is a computed roll-up of every effort line — it's read-only because edits here can't be split back across lines. Switch to Estimate-Level, Implementation, Hypercare, or AMS to edit."
              style={{ fontSize: 10.5, background: 'rgba(59,130,246,0.18)', color: '#60A5FA', border: '1px solid rgba(59,130,246,0.3)', padding: '2px 8px', borderRadius: 10 }}
            >
              Consolidated · read-only
            </span>
          )}
          {effortLineId && <span className="badge" style={{ fontSize: 10.5, background: 'rgba(139,92,246,0.18)', color: '#A78BFA', border: '1px solid rgba(139,92,246,0.3)', padding: '2px 8px', borderRadius: 10 }}>Effort Line</span>}
          {dirty && !consolidated && <span className="badge" style={{ fontSize: 10.5, background: 'rgba(250,204,21,0.15)', color: '#FACC15', border: '1px solid rgba(250,204,21,0.3)', padding: '2px 8px', borderRadius: 10 }}>Unsaved</span>}
          {isLocked && !consolidated && <span className="badge" style={{ fontSize: 10.5, background: 'rgba(16,185,129,0.15)', color: '#10B981', border: '1px solid rgba(16,185,129,0.3)', padding: '2px 8px', borderRadius: 10 }}>Locked</span>}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'nowrap' }}>
          {/* Columns ▾ — full visibility control for every grid column.
              Top three toggles are groups (Details / Cost / Extras); the rest
              are individual columns between the month allocations and the
              actions column. */}
          <ToolbarMenu label={(() => {
            const groupOn = [showDetailCols, showCostCols, showExtrasDefaults].filter(Boolean).length;
            const colOn = Object.values(colVis).filter(Boolean).length;
            return `Columns · ${groupOn + colOn}`;
          })()}>
            <div style={{ padding: '2px 10px', fontSize: 10, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Groups</div>
            <MenuCheck checked={showDetailCols} onChange={e => setShowDetailCols(e.target.checked)} title="Product, Price Type, Source, FTE columns">Details</MenuCheck>
            <MenuCheck checked={showCostCols} onChange={e => setShowCostCols(e.target.checked)} title="Cost, Overhead, Total Cost, Margin columns">Cost</MenuCheck>
            <MenuCheck checked={showExtrasDefaults} onChange={e => setShowExtrasDefaults(e.target.checked)} title="Default Extras accordion">Extras (defaults bar)</MenuCheck>
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', margin: '4px 0' }} />
            <div style={{ padding: '2px 10px', fontSize: 10, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Columns</div>
            <MenuCheck checked={colVis.periods}  onChange={() => toggleCol('periods')}  title={`Period columns (M1…Mn) — shown as ${settings.effortUnit === 'week' ? 'weeks' : 'months'}, split by the ${allocMode} mode above`}>Periods ({settings.effortUnit === 'week' ? 'Weeks' : 'Months'})</MenuCheck>
            <MenuCheck checked={colVis.hours}    onChange={() => toggleCol('hours')}    title="Computed hours per resource (Off Hrs / On Hrs depending on mode)">Hours</MenuCheck>
            <MenuCheck checked={colVis.travels}  onChange={() => toggleCol('travels')}  title="Travels column">Travels</MenuCheck>
            <MenuCheck checked={colVis.rate}     onChange={() => toggleCol('rate')}     title="Rate columns (Off Rate / On Rate depending on mode)">Rate</MenuCheck>
            <MenuCheck checked={colVis.amount}   onChange={() => toggleCol('amount')}   title="Amount columns (Off Amt / On Amt depending on mode)">Amount</MenuCheck>
            <MenuCheck checked={colVis.totalAmt} onChange={() => toggleCol('totalAmt')} title="Per-line Total Amount column">Total Amt</MenuCheck>
            <MenuCheck checked={colVis.extras}   onChange={() => toggleCol('extras')}   title="Per-line Extras column">Extras</MenuCheck>
            <MenuCheck checked={colVis.grandTotal} onChange={() => toggleCol('grandTotal')} title="Per-line Grand Total (Amount + Extras)">Grand Total</MenuCheck>

            <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', margin: '4px 0' }} />
            <div style={{ padding: '2px 10px', fontSize: 10, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              System Variants
            </div>
            <div style={{ padding: '0 10px 4px', fontSize: 10.5, color: '#64748B', lineHeight: 1.35 }}>
              Radio = default for new cost sheets. Click the name to apply it here.
            </div>
            {variants.length === 0 ? (
              <div style={{ padding: '6px 10px', fontSize: 11.5, color: '#64748B' }}>No saved variants yet.</div>
            ) : variants.map(v => (
              <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <label
                  title={v.is_default
                    ? 'Default variant — applied to newly-created cost sheets'
                    : 'Make this the default for new cost sheets'}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    padding: '0 6px', cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="cs-default-variant"
                    checked={!!v.is_default}
                    onChange={() => setVariantDefault(v.id)}
                    style={{ accentColor: '#60A5FA', cursor: 'pointer' }}
                  />
                </label>
                <button
                  onClick={() => applyVariant(v)}
                  title="Apply this variant to the current view"
                  style={{
                    all: 'unset', flex: 1, cursor: 'pointer',
                    padding: '6px 10px', borderRadius: 5,
                    fontSize: 12.5, color: '#E5E7EB',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {v.name}
                  </span>
                  {v.is_default && (
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                      background: 'rgba(16,185,129,0.18)', color: '#10B981',
                    }}>DEFAULT</span>
                  )}
                </button>
                <button
                  onClick={() => deleteVariant(v.id)}
                  title="Delete variant"
                  style={{
                    all: 'unset', cursor: 'pointer', color: '#EF4444',
                    padding: '4px 8px', fontSize: 14,
                  }}
                >×</button>
              </div>
            ))}
            <div style={{ padding: '6px 6px 0', marginTop: 4, borderTop: '1px solid rgba(255,255,255,0.04)' }}>
              <button
                onClick={saveVariant}
                title="Save the current column selection as a system-wide variant"
                style={{
                  width: '100%', padding: '6px 8px', fontSize: 11, fontWeight: 600,
                  borderRadius: 5, border: '1px solid rgba(59,141,248,0.35)',
                  background: 'rgba(59,141,248,0.10)', color: '#60A5FA', cursor: 'pointer',
                }}
              >+ Save current view as System Variant</button>
            </div>
          </ToolbarMenu>

          {/* Spread Effort — copy FTE to all M1..Mn based on duration + allocMode */}
          <button
            className="cs-mode-btn"
            onClick={spreadEffort}
            title="Spread Effort — copy each line's FTE across every month (M1…Mn) based on Duration and the current allocation mode"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="17 3 21 3 21 7" />
              <line x1="21" y1="3" x2="13" y2="11" />
              <polyline points="7 21 3 21 3 17" />
              <line x1="3" y1="21" x2="11" y2="13" />
              <polyline points="17 21 21 21 21 17" />
              <line x1="21" y1="21" x2="13" y2="13" />
              <polyline points="7 3 3 3 3 7" />
              <line x1="3" y1="3" x2="11" y2="11" />
            </svg>
            Spread Effort
          </button>

          {/* Allocation mode — compact segmented control.
              Switching to Off (offshore-only) zeroes the Onsite Months on every
              resource; switching to On (onsite-only) zeroes the Offshore Months.
              Mix / Hide leave the values alone. */}
          <div className="cs-mode-group" title="Which allocation columns to show">
            {[
              ['mixed', 'Mix'],
              ['offshore', 'Off'],
              ['onsite', 'On'],
              ['hidden', 'Hide'],
            ].map(([k, lbl]) => (
              <button
                key={k}
                className={`cs-mode-btn${allocMode === k ? ' active' : ''}`}
                onClick={() => {
                  if (k === allocMode) return;
                  if (k === 'offshore' || k === 'onsite') {
                    const clearField = k === 'offshore' ? 'onsiteMonths' : 'offshoreMonths';
                    setResources(prev => prev.map(r => ({
                      ...r,
                      [clearField]: (r[clearField] || []).map(() => 0),
                    })));
                    setDirty(true);
                  }
                  setAllocMode(k);
                }}
              >{lbl}</button>
            ))}
          </div>

          {/* Versions */}
          <button className="cs-mode-btn" onClick={() => setShowVersions(!showVersions)} title="Saved versions">
            {showVersions ? 'Versions ▴' : `Versions · ${versions.length}`}
          </button>

          {/* Export ▾ */}
          <ToolbarMenu label="Export">
            {({ close }) => (
              <>
                <MenuItem onClick={() => { exportToExcel('costSheet'); close(); }} title="Full Cost Sheet with employee costs, extras">Cost Sheet (xlsx)</MenuItem>
                <MenuItem onClick={() => { exportToExcel('pricingSheet'); close(); }} title="Pricing Sheet with effort and pricing only">Pricing Sheet (xlsx)</MenuItem>
              </>
            )}
          </ToolbarMenu>

          {/* Save / Lock */}
          {consolidated ? null : isLocked ? (
            <button className="btn btn-secondary" onClick={handleUnlock} disabled={saving}>Unlock</button>
          ) : (
            <div className="cs-mode-group">
              <button className="cs-mode-btn" onClick={() => handleSave('draft')} disabled={saving}>
                {saving ? '…' : 'Draft'}
              </button>
              <button className="cs-mode-btn active" onClick={() => handleSave('locked')} disabled={saving} title="Save and lock — creates soft allocations for assigned employees">
                Save & Lock
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ─── Version Panel ─── */}
      {showVersions && (
        <div className="cs-version-panel">
          <div className="cs-version-save">
            {(() => {
              // Auto-generate version prefix: V{n}-DD-MMM-YY
              const nextNum = versions.length + 1;
              const now = new Date();
              const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
              const dateStr = `${String(now.getDate()).padStart(2, '0')}-${months[now.getMonth()]}-${String(now.getFullYear()).slice(-2)}`;
              const prefix = `V${nextNum}-${dateStr}`;
              return (
                <>
                  <span className="cs-version-prefix" title="Auto-generated version prefix">{prefix}-</span>
                  <input className="cs-input" placeholder="Short description (e.g. Discussion with Niyant)" value={versionNotes} onChange={e => setVersionNotes(e.target.value)} style={{ flex: 1 }} />
                  <button className="btn btn-primary" onClick={() => {
                    const label = `${prefix}${versionNotes.trim() ? '-' + versionNotes.trim() : ''}`;
                    setVersionLabel(label);
                    // Save immediately with computed label
                    (async () => {
                      setSaving(true);
                      try {
                        await apiFetch(`/api/estimates/effort-estimates/${estimateId}/cost-sheet-versions`, {
                          method: 'POST',
                          body: JSON.stringify({
                            versionLabel: label,
                            costSheet: { settings, resources },
                            totalAmount: grandTotal,
                            proposedValue,
                            notes: versionNotes
                          })
                        });
                        setVersionLabel(''); setVersionNotes('');
                        loadVersions();
                      } catch (e) { /* ignore */ }
                      setSaving(false);
                    })();
                  }} disabled={saving}>Save Version</button>
                </>
              );
            })()}
          </div>
          {versions.length > 0 && (
            <>
              <table className="cs-version-table">
                <thead><tr><th></th><th>Label</th><th>Total</th><th>Proposed</th><th>Notes</th><th>Created</th><th>Actions</th></tr></thead>
                <tbody>
                  {versions.map(v => (
                    <tr key={v.id}>
                      <td><input type="checkbox" checked={compareIds.includes(v.id)} onChange={() => toggleCompare(v.id)} disabled={!compareIds.includes(v.id) && compareIds.length >= 3} /></td>
                      <td style={{ fontWeight: 500 }}>{v.version_label}</td>
                      <td style={{ textAlign: 'right' }}>{fmtNum(v.total_amount, 0)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtNum(v.proposed_value, 0)}</td>
                      <td>{v.notes || '—'}</td>
                      <td>{formatDate(v.created_at)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="cs-btn-sm" onClick={() => viewVersion(v.id)} title="View">👁</button>
                          <button className="cs-btn-sm" onClick={() => restoreVersion(v.id)} title="Restore">↩</button>
                          <button className="cs-btn-sm cs-btn-danger" onClick={() => deleteVersion(v.id)} title="Delete">✕</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {compareIds.length >= 2 && (
                <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="btn btn-primary" onClick={startComparison}>
                    Compare {compareIds.length} Versions
                  </button>
                </div>
              )}
            </>
          )}
          {versions.length === 0 && <p style={{ color: '#94A3B8', fontSize: 13, margin: '8px 0 0' }}>No saved versions yet. Save a snapshot above.</p>}
        </div>
      )}

      {/* ─── Settings Bar (accordion) ─── */}
      <button
        type="button"
        onClick={() => setSettingsOpen(o => !o)}
        className="cs-accordion-head"
        title="Show/hide cost-sheet settings"
      >
        <span className="cs-accordion-caret">{settingsOpen ? '▼' : '▶'}</span>
        <span>Cost Sheet Settings</span>
        <span className="cs-accordion-hint">
          {(() => {
            // Compact summary shown on the collapsed accordion.
            const bits = [];
            bits.push(settings.billingCurrency);
            if (settings.startDate) {
              const d = new Date(settings.startDate);
              if (!Number.isNaN(d.getTime())) {
                bits.push(d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }));
              }
            }
            bits.push(`${settings.months || 0}mo`);
            const ratePerLabel = (RATE_PER_OPTIONS.find(o => o.value === settings.ratePer) || {}).label || '';
            if (ratePerLabel) bits.push(ratePerLabel);

            // Only include the rate(s) relevant to the current alloc mode.
            const sc = settings.rateScale || '1';
            const scLbl = scaleLabel(sc);
            const sym = getCurrencySymbol(settings.billingCurrency);
            const amtLabel = scLbl ? `${scLbl}${sym}` : sym;
            const fmtR = v => {
              const n = Math.round(toDisplay(parseFloat(v) || 0, sc) * 1000) / 1000;
              return n === 0 ? '0' : n;
            };
            if (allocMode === 'offshore') {
              bits.push(`Off ${amtLabel}${fmtR(settings.defaultOffshoreRate)}`);
            } else if (allocMode === 'onsite') {
              bits.push(`On ${amtLabel}${fmtR(settings.defaultOnsiteRate)}`);
            } else if (allocMode === 'mixed') {
              bits.push(`Off ${amtLabel}${fmtR(settings.defaultOffshoreRate)} / On ${amtLabel}${fmtR(settings.defaultOnsiteRate)}`);
            }

            const scaleLbl = (SCALE_OPTIONS.find(o => o.value === String(settings.rateScale)) || {}).label;
            if (scaleLbl && scaleLbl !== 'Absolute') bits.push(scaleLbl);

            return bits.join(' · ');
          })()}
        </span>
      </button>
      {settingsOpen && <div className="cs-settings-bar">
        {showProductSection && <>
          <div className="cs-setting">
            <label>Products (multi-select)</label>
            <MultiTagSelect
              value={settingsProductIds}
              options={productOptions}
              placeholder="— None —"
              width={180}
              readOnly={isLocked}
              onChange={ids => {
                // Write both the new `productIds` (canonical) and blank out the
                // legacy `productId` so stale values never reappear.
                updateSetting('productIds', ids);
                if (settings.productId) updateSetting('productId', '');
              }}
            />
          </div>
          <div className="cs-setting-divider" />
        </>}
        <div className="cs-setting">
          <label>Billing Currency</label>
          <select className="cs-input" value={settings.billingCurrency} onChange={e => updateSetting('billingCurrency', e.target.value)} disabled={isLocked}>
            {CURRENCY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="cs-setting">
          <label>Display Currency</label>
          <select className="cs-input" value={settings.displayCurrency} onChange={e => updateSetting('displayCurrency', e.target.value)} disabled={isLocked}>
            {CURRENCY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="cs-setting">
          <label>Exchange Rate {settings.billingCurrency === settings.displayCurrency ? '' : `(${settings.billingCurrency}→${settings.displayCurrency})`}</label>
          {settings.billingCurrency === settings.displayCurrency ? (
            <span className="cs-readonly" style={{ width: 70, textAlign: 'center', color: '#64748B', fontSize: 11 }}>Same</span>
          ) : (
            <input className={`cs-input${rateWarning ? ' cs-input-warn' : ''}`} type="number" value={settings.exchangeRate} onChange={e => updateSetting('exchangeRate', parseFloat(e.target.value) || 0)} style={{ width: 70 }} min="0" step="0.01" disabled={isLocked} />
          )}
        </div>
        {rateWarning && (
          <div className="cs-rate-warning">{rateWarning}</div>
        )}
        <div className="cs-setting">
          <label>Mandays/Month</label>
          <input className="cs-input" type="number" value={settings.mandaysPerMonth} onChange={e => updateSetting('mandaysPerMonth', parseInt(e.target.value, 10) || 20)} style={{ width: 55 }} min="1" disabled={isLocked} />
        </div>
        <div className="cs-setting">
          <label>Hrs/Day</label>
          <input className="cs-input" type="number" value={settings.hoursPerDay} onChange={e => updateSetting('hoursPerDay', parseInt(e.target.value, 10) || 8)} style={{ width: 50 }} min="1" disabled={isLocked} />
        </div>
        <div className="cs-setting">
          <label>Offshore Rate ({amtLabel}/{(settings.ratePer || 'ManHour').replace('Man', '').toLowerCase()})</label>
          <input className="cs-input" type="number" value={Math.round(toDisplay(settings.defaultOffshoreRate, sc) * 1000) / 1000} onChange={e => updateSetting('defaultOffshoreRate', toAbsolute(e.target.value, sc))} style={{ width: 70 }} min="0" step={sc === '1' ? '1' : '0.1'} disabled={isLocked} />
        </div>
        <div className="cs-setting">
          <label>Onsite Rate ({amtLabel}/{(settings.ratePer || 'ManHour').replace('Man', '').toLowerCase()})</label>
          <input className="cs-input" type="number" value={Math.round(toDisplay(settings.defaultOnsiteRate, sc) * 1000) / 1000} onChange={e => updateSetting('defaultOnsiteRate', toAbsolute(e.target.value, sc))} style={{ width: 70 }} min="0" step={sc === '1' ? '1' : '0.1'} disabled={isLocked} />
        </div>
        {!isLocked && (
          <div className="cs-setting">
            <label>&nbsp;</label>
            <button
              className="cs-mode-btn"
              onClick={applyDefaultRatesToAll}
              title="Apply the Offshore & Onsite rates above to every existing line. New lines already pick up these defaults automatically."
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 28 }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              Apply Rate
            </button>
          </div>
        )}
        <div className="cs-setting">
          <label>Start Date</label>
          <input className="cs-input" type="date" value={settings.startDate || ''} onChange={e => updateSetting('startDate', e.target.value)} style={{ width: 130 }} disabled={isLocked} />
        </div>
        <div className="cs-setting">
          <label>Duration (Months)</label>
          <input className="cs-input" type="number" value={settings.months} onChange={e => updateSetting('months', parseInt(e.target.value, 10) || 3)} style={{ width: 50 }} min="1" max="24" disabled={isLocked} />
        </div>
        <div className="cs-setting">
          <label>Effort Display</label>
          <select className="cs-input" value={settings.effortUnit || 'month'} onChange={e => updateSetting('effortUnit', e.target.value)} disabled={isLocked}>
            {EFFORT_UNIT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="cs-setting">
          <label className="cs-toggle-label">
            <input type="checkbox" checked={!!settings.showActualPeriod} onChange={e => updateSetting('showActualPeriod', e.target.checked)} disabled={isLocked || !settings.startDate} />
            Actual Period
          </label>
        </div>
        <div className="cs-setting-divider" />
        <div className="cs-setting">
          <label>Discount %</label>
          <input className="cs-input" type="number" value={settings.discountPct} onChange={e => updateSetting('discountPct', parseFloat(e.target.value) || 0)} style={{ width: 55 }} min="0" max="100" disabled={isLocked} />
        </div>
        <div className="cs-setting">
          <label>Overhead %</label>
          <input className="cs-input" type="number" value={settings.overheadPct} onChange={e => updateSetting('overheadPct', parseFloat(e.target.value) || 0)} style={{ width: 55 }} min="0" max="100" disabled={isLocked} />
        </div>
        <div className="cs-setting-divider" />
        <div className="cs-setting">
          <label>Default Rate Per</label>
          <select className="cs-input" value={settings.ratePer || 'ManHour'} onChange={e => updateSetting('ratePer', e.target.value)} disabled={isLocked}>
            {RATE_PER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="cs-setting">
          <label>Rate Scale</label>
          <select className="cs-input cs-scale-select" value={settings.rateScale || '1'} onChange={e => updateSetting('rateScale', e.target.value)} disabled={isLocked}>
            {SCALE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>}

      {/* ─── Default Extras Bar (accordion) ─── */}
      {showExtrasDefaults && (
        <button
          type="button"
          onClick={() => setExtrasOpen(o => !o)}
          className="cs-accordion-head"
          title="Show/hide default extras"
        >
          <span className="cs-accordion-caret">{extrasOpen ? '▼' : '▶'}</span>
          <span>Default Extras</span>
          <span className="cs-accordion-hint">
            Per-Diem {settings.defaultPerDiem || 0} · Visa {settings.defaultVisaRate || 0} · Lodging {settings.defaultLodging || 0}
          </span>
        </button>
      )}
      {showExtrasDefaults && extrasOpen && <div className="cs-extras-defaults-bar">
        <div className="cs-extras-defaults-label">Default Extras</div>
        <div className="cs-extra-item">
          <label>Per Diem (rate/day)</label>
          <input className="cs-input" type="number" value={settings.defaultPerDiem} onChange={e => updateSetting('defaultPerDiem', parseFloat(e.target.value) || 0)} style={{ width: 70 }} min="0" disabled={isLocked} />
          <span className="cs-extra-calc">= {fmtScaled((settings.defaultPerDiem || 0) * settings.mandaysPerMonth * settings.months, sc, sym)}</span>
        </div>
        <div className="cs-extra-item">
          <label>Visa (per trip)</label>
          <input className="cs-input" type="number" value={settings.defaultVisaRate} onChange={e => updateSetting('defaultVisaRate', parseFloat(e.target.value) || 0)} style={{ width: 70 }} min="0" disabled={isLocked} />
        </div>
        <div className="cs-extra-item">
          <label>Conveyance (fixed)</label>
          <input className="cs-input" type="number" value={settings.defaultConveyance} onChange={e => updateSetting('defaultConveyance', parseFloat(e.target.value) || 0)} style={{ width: 70 }} min="0" disabled={isLocked} />
        </div>
        <div className="cs-extra-item">
          <label>Lodging (rate/day)</label>
          <input className="cs-input" type="number" value={settings.defaultLodging} onChange={e => updateSetting('defaultLodging', parseFloat(e.target.value) || 0)} style={{ width: 70 }} min="0" disabled={isLocked} />
        </div>
        <div className="cs-extra-item">
          <label>Boarding (rate/day)</label>
          <input className="cs-input" type="number" value={settings.defaultBoarding} onChange={e => updateSetting('defaultBoarding', parseFloat(e.target.value) || 0)} style={{ width: 70 }} min="0" disabled={isLocked} />
        </div>
        {!isLocked && (
          <button className="btn btn-secondary cs-apply-defaults-btn" onClick={() => {
            if (!window.confirm('Apply these default extras rates to ALL existing lines?')) return;
            setResources(prev => prev.map(r => ({
              ...r,
              perDiemRate: settings.defaultPerDiem,
              visaRate: settings.defaultVisaRate,
              conveyanceRate: settings.defaultConveyance,
              lodgingRate: settings.defaultLodging,
              boardingRate: settings.defaultBoarding,
            })));
            setDirty(true);
          }} title="Apply defaults to all existing resource lines">Apply to All</button>
        )}
      </div>}

      {/* ─── Resource Table ─── */}
      <div className="cs-table-wrap">
        <table className="cs-table">
          <thead>
            {(() => {
              const showOff = allocMode === 'mixed' || allocMode === 'offshore';
              const showOn = allocMode === 'mixed' || allocMode === 'onsite';
              const hasMonthRow = showOff || showOn;
              const pc = getPeriodCount(settings);
              const rateUnit = (settings.ratePer || 'ManHour').replace('Man', '').toLowerCase();
              return (
                <>
                  <tr>
                    <th rowSpan={hasMonthRow ? 2 : 1} style={{ width: 30 }}>#</th>
                    <th rowSpan={hasMonthRow ? 2 : 1} style={{ width: 140 }}>Role</th>
                    <th rowSpan={hasMonthRow ? 2 : 1} style={{ width: 55 }}>Level</th>
                    <th rowSpan={hasMonthRow ? 2 : 1} style={{ width: 170 }}>Employee</th>
                    <th rowSpan={hasMonthRow ? 2 : 1} style={{ width: 130 }}>Skill Group</th>
                    {showDetailCols && <th rowSpan={hasMonthRow ? 2 : 1} style={{ width: 130 }}>Product</th>}
                    {showDetailCols && <th rowSpan={hasMonthRow ? 2 : 1} style={{ width: 50 }}>FTE</th>}
                    {colVis.periods && showOff && <th colSpan={pc} className="cs-th-offshore-group">Offshore</th>}
                    {colVis.periods && showOn && <th colSpan={pc} className="cs-th-onsite-group">Onsite</th>}
                    {colVis.hours && showOff && <th rowSpan={hasMonthRow ? 2 : 1} style={{ width: 50 }}>Off Hrs</th>}
                    {colVis.hours && showOn  && <th rowSpan={hasMonthRow ? 2 : 1} style={{ width: 50 }}>On Hrs</th>}
                    {colVis.travels && <th rowSpan={hasMonthRow ? 2 : 1} style={{ width: 50 }}>Travels</th>}
                    {colVis.rate   && showOff && <th rowSpan={hasMonthRow ? 2 : 1} style={{ width: 55 }} className="cs-th-offshore">Off Rate ({amtLabel}/{rateUnit})</th>}
                    {colVis.amount && showOff && <th rowSpan={hasMonthRow ? 2 : 1} style={{ width: 60 }} className="cs-th-offshore">Off Amt ({amtLabel})</th>}
                    {colVis.rate   && showOn  && <th rowSpan={hasMonthRow ? 2 : 1} style={{ width: 55 }} className="cs-th-onsite">On Rate ({amtLabel}/{rateUnit})</th>}
                    {colVis.amount && showOn  && <th rowSpan={hasMonthRow ? 2 : 1} style={{ width: 60 }} className="cs-th-onsite">On Amt ({amtLabel})</th>}
                    {colVis.totalAmt   && <th rowSpan={hasMonthRow ? 2 : 1} style={{ width: 65 }}>Total Amt ({amtLabel})</th>}
                    {colVis.extras     && <th rowSpan={hasMonthRow ? 2 : 1} style={{ width: 60 }}>Extras ({amtLabel})</th>}
                    {colVis.grandTotal && <th rowSpan={hasMonthRow ? 2 : 1} style={{ width: 65 }}>Total ({amtLabel})</th>}
                    {showCostCols && <th rowSpan={hasMonthRow ? 2 : 1} style={{ width: 85 }} className="cs-th-cost">Cost</th>}
                    {showCostCols && <th rowSpan={hasMonthRow ? 2 : 1} style={{ width: 75 }} className="cs-th-cost">Overhead</th>}
                    {showCostCols && <th rowSpan={hasMonthRow ? 2 : 1} style={{ width: 85 }} className="cs-th-cost">Total Cost</th>}
                    {showCostCols && <th rowSpan={hasMonthRow ? 2 : 1} style={{ width: 85 }} className="cs-th-cost">Margin</th>}
                    {showDetailCols && <th rowSpan={hasMonthRow ? 2 : 1} style={{ width: 90 }}>Price Type</th>}
                    {showDetailCols && <th rowSpan={hasMonthRow ? 2 : 1} style={{ width: 80 }}>Source</th>}
                    <th rowSpan={hasMonthRow ? 2 : 1} style={{ width: 50 }}></th>
                  </tr>
                  {hasMonthRow && colVis.periods && (
                    <tr>
                      {showOff && Array.from({ length: pc }, (_, i) => (
                        <th key={`oh${i}`} className="cs-th-offshore" style={{ width: 58, fontSize: settings.showActualPeriod ? 10 : 12 }}>{getPeriodLabel(i, settings)}</th>
                      ))}
                      {showOn && Array.from({ length: pc }, (_, i) => (
                        <th key={`on${i}`} className="cs-th-onsite" style={{ width: 58, fontSize: settings.showActualPeriod ? 10 : 12 }}>{getPeriodLabel(i, settings)}</th>
                      ))}
                    </tr>
                  )}
                </>
              );
            })()}
          </thead>
          <tbody>
            {resources.map((res, idx) => {
              const offH = calcOffshoreHrs(res, settings);
              const onsH = calcOnsiteHrs(res, settings);
              const amt = calcAmount(res, settings);
              const ext = calcExtras(res, settings);
              const total = amt + ext;
              // Line-level products (array, with fallback to legacy single value)
              const linePids = asArr(res.productIds && res.productIds.length ? res.productIds : res.productId);
              const lineMids = asArr(res.moduleIds  && res.moduleIds.length  ? res.moduleIds  : res.moduleId);
              const lineModuleOptions = moduleOptionsForLine(linePids);
              const showOff = allocMode === 'mixed' || allocMode === 'offshore';
              const showOn = allocMode === 'mixed' || allocMode === 'onsite';
              const pc = getPeriodCount(settings);
              const monthColCount = (showOff ? pc : 0) + (showOn ? pc : 0);

              // Cost calculations
              const lineCost = calcLineCost(res);
              const lineOverhead = lineCost * ((settings.overheadPct || 0) / 100);
              const lineTotalCost = lineCost + lineOverhead;
              const lineMargin = amt - lineTotalCost; // Amount (effort) - total cost

              // Employees for this line
              const lineEmployees = getEmployeesForLine(res);
              const maxEmpCount = Math.ceil(parseFloat(res.fte) || 1);

              return (
                <React.Fragment key={idx}>
                  <tr className={res.priceType === 'Excluded' ? 'cs-row-excluded' : ''}>
                    <td className="cs-row-num">{idx + 1}</td>
                    <td>
                      <EditCell value={res.role} onChange={v => updateResource(idx, 'role', v)} options={roleOptions} width={130} readOnly={isLocked} />
                    </td>
                    <td>
                      <EditCell value={res.level || ''} onChange={v => updateResource(idx, 'level', v)} options={LEVEL_OPTIONS.map(l => ({ value: l, label: l || '—' }))} width={50} readOnly={isLocked} />
                    </td>
                    <td>
                      <EmployeeMultiSelect
                        selectedIds={res.employeeIds || []}
                        onChange={ids => updateResource(idx, 'employeeIds', ids)}
                        employees={lineEmployees}
                        maxCount={maxEmpCount}
                        disabled={isLocked}
                      />
                    </td>
                    <td>
                      <MultiTagSelect
                        value={lineMids}
                        options={lineModuleOptions}
                        placeholder="— None —"
                        width={130}
                        readOnly={isLocked}
                        onChange={ids => {
                          updateResource(idx, 'moduleIds', ids);
                          if (res.moduleId) updateResource(idx, 'moduleId', '');
                        }}
                      />
                    </td>
                    {showDetailCols && <td>
                      <MultiTagSelect
                        value={linePids}
                        options={productOptions}
                        placeholder="— None —"
                        width={130}
                        readOnly={isLocked}
                        onChange={ids => {
                          updateResource(idx, 'productIds', ids);
                          if (res.productId) updateResource(idx, 'productId', '');
                        }}
                      />
                    </td>}
                    {showDetailCols && <td>
                      <EditCell value={res.fte} onChange={v => updateResource(idx, 'fte', v)} type="number" width={52} min="0" step="0.5" readOnly={isLocked} />
                    </td>}
                    {colVis.periods && showOff && res.offshoreMonths.slice(0, pc).map((v, mi) => {
                      const fte = parseFloat(res.fte) || 0;
                      const maxP = getMaxFteForPeriod(mi, fte, settings);
                      const onsVal = parseFloat(res.onsiteMonths[mi]) || 0;
                      const offVal = parseFloat(v) || 0;
                      const overFte = (offVal + onsVal) > maxP + 0.001;
                      return (
                        <td key={`o${mi}`} className="cs-td-offshore">
                          <EditCell value={v} onChange={val => updateMonthAlloc(idx, 'offshoreMonths', mi, val)} type="number" width={52} min="0" step="0.25" className={overFte ? 'cs-input-warn' : ''} readOnly={isLocked} />
                        </td>
                      );
                    })}
                    {colVis.periods && showOn && res.onsiteMonths.slice(0, pc).map((v, mi) => {
                      const fte = parseFloat(res.fte) || 0;
                      const maxP = getMaxFteForPeriod(mi, fte, settings);
                      const offVal = parseFloat(res.offshoreMonths[mi]) || 0;
                      const onsVal = parseFloat(v) || 0;
                      const overFte = (offVal + onsVal) > maxP + 0.001;
                      return (
                        <td key={`n${mi}`} className="cs-td-onsite">
                          <EditCell value={v} onChange={val => updateMonthAlloc(idx, 'onsiteMonths', mi, val)} type="number" width={52} min="0" step="0.25" className={overFte ? 'cs-input-warn' : ''} readOnly={isLocked} />
                        </td>
                      );
                    })}
                    {colVis.hours && showOff && <td className="cs-calc">{fmtNum(offH)}</td>}
                    {colVis.hours && showOn  && <td className="cs-calc">{fmtNum(onsH)}</td>}
                    {colVis.travels && <td>
                      <EditCell value={res.returnTravel} onChange={v => updateResource(idx, 'returnTravel', v)} type="number" width={52} min="0" readOnly={isLocked} />
                    </td>}
                    {colVis.rate && showOff && <td>
                      <EditCell value={Math.round(toDisplay(res.offshoreRate, sc) * 1000) / 1000} onChange={v => updateResource(idx, 'offshoreRate', toAbsolute(v, sc))} type="number" width={60} min="0" step={sc === '1' ? '1' : '0.1'} readOnly={isLocked} />
                    </td>}
                    {colVis.amount && showOff && <td className="cs-calc cs-amount">{fmtNum(toDisplay(calcOffshoreAmt(res, settings), sc), scDec)}</td>}
                    {colVis.rate && showOn && <td>
                      <EditCell value={Math.round(toDisplay(res.onsiteRate, sc) * 1000) / 1000} onChange={v => updateResource(idx, 'onsiteRate', toAbsolute(v, sc))} type="number" width={60} min="0" step={sc === '1' ? '1' : '0.1'} readOnly={isLocked} />
                    </td>}
                    {colVis.amount && showOn && <td className="cs-calc cs-amount">{fmtNum(toDisplay(calcOnsiteAmt(res, settings), sc), scDec)}</td>}
                    {colVis.totalAmt && <td className="cs-calc cs-amount" style={{ fontWeight: 600 }}>{fmtNum(toDisplay(amt, sc), scDec)}</td>}
                    {colVis.extras && <td className="cs-calc">
                      <span className="cs-extras-toggle" onClick={() => toggleExtras(idx)} title="Click to expand extras">
                        {fmtNum(toDisplay(ext, sc), scDec)} {expandedExtras[idx] ? '▾' : '▸'}
                      </span>
                    </td>}
                    {colVis.grandTotal && <td className="cs-calc cs-total">{fmtNum(toDisplay(total, sc), scDec)}</td>}
                    {showCostCols && <td className="cs-calc cs-td-cost">{lineCost > 0 ? fmtNum(toDisplay(lineCost, sc), scDec) : '—'}</td>}
                    {showCostCols && <td className="cs-calc cs-td-cost">{lineOverhead > 0 ? fmtNum(toDisplay(lineOverhead, sc), scDec) : '—'}</td>}
                    {showCostCols && <td className="cs-calc cs-td-cost" style={{ fontWeight: 600 }}>{lineTotalCost > 0 ? fmtNum(toDisplay(lineTotalCost, sc), scDec) : '—'}</td>}
                    {showCostCols && <td className={`cs-calc cs-td-cost${lineMargin < 0 ? ' cs-margin-neg' : ' cs-margin-pos'}`} style={{ fontWeight: 600 }}>{lineTotalCost > 0 ? fmtNum(toDisplay(lineMargin, sc), scDec) : '—'}</td>}
                    {showDetailCols && <td>
                      <EditCell value={res.priceType} onChange={v => updateResource(idx, 'priceType', v)} options={PRICE_TYPE} width={85} readOnly={isLocked} />
                    </td>}
                    {showDetailCols && <td>
                      <EditCell value={res.source} onChange={v => updateResource(idx, 'source', v)} options={SOURCE_OPTIONS} width={75} readOnly={isLocked} />
                    </td>}
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {!isLocked && (
                        <>
                          <button
                            className="cs-btn-sm"
                            onClick={() => duplicateResource(idx)}
                            title="Copy this line"
                            style={{ marginRight: 4 }}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                          </button>
                          <button className="cs-btn-sm cs-btn-danger" onClick={() => removeResource(idx)} title="Remove row">✕</button>
                        </>
                      )}
                    </td>
                  </tr>
                  {/* Extras expansion row */}
                  {expandedExtras[idx] && (
                    <tr className="cs-extras-row">
                      <td></td>
                      <td colSpan={(function () {
                        // Dynamically count the remaining columns on this row
                        // so the extras panel always spans the full grid.
                        let n = 4; // Role, Level, Employee, Skill Group
                        if (showDetailCols) n += 2; // Product, FTE
                        if (colVis.periods) n += monthColCount;
                        if (colVis.hours)   n += (showOff ? 1 : 0) + (showOn ? 1 : 0);
                        if (colVis.travels) n += 1;
                        if (colVis.rate)    n += (showOff ? 1 : 0) + (showOn ? 1 : 0);
                        if (colVis.amount)  n += (showOff ? 1 : 0) + (showOn ? 1 : 0);
                        if (colVis.totalAmt)   n += 1;
                        if (colVis.extras)     n += 1;
                        if (colVis.grandTotal) n += 1;
                        if (showCostCols) n += 4;
                        if (showDetailCols) n += 2; // Price Type, Source
                        n += 1; // actions column
                        return n;
                      })()}>
                        <div className="cs-extras-grid">
                          <div className="cs-extra-item">
                            <label>Per Diem (rate/day)</label>
                            <input className="cs-input" type="number" value={res.perDiemRate} onChange={e => updateResource(idx, 'perDiemRate', e.target.value)} style={{ width: 70 }} min="0" disabled={isLocked} />
                            <span className="cs-extra-calc">= {fmtScaled(calcPerDiem(res, settings), sc, sym)}</span>
                          </div>
                          <div className="cs-extra-item">
                            <label>Visa (per trip)</label>
                            <input className="cs-input" type="number" value={res.visaRate} onChange={e => updateResource(idx, 'visaRate', e.target.value)} style={{ width: 70 }} min="0" disabled={isLocked} />
                            <span className="cs-extra-calc">= {fmtScaled(calcVisa(res), sc, sym)}</span>
                          </div>
                          <div className="cs-extra-item">
                            <label>Conveyance (fixed)</label>
                            <input className="cs-input" type="number" value={res.conveyanceRate} onChange={e => updateResource(idx, 'conveyanceRate', e.target.value)} style={{ width: 70 }} min="0" disabled={isLocked} />
                          </div>
                          <div className="cs-extra-item">
                            <label>Lodging (rate/day)</label>
                            <input className="cs-input" type="number" value={res.lodgingRate} onChange={e => updateResource(idx, 'lodgingRate', e.target.value)} style={{ width: 70 }} min="0" disabled={isLocked} />
                            <span className="cs-extra-calc">= {fmtScaled(calcLodging(res, settings), sc, sym)}</span>
                          </div>
                          <div className="cs-extra-item">
                            <label>Boarding (rate/day)</label>
                            <input className="cs-input" type="number" value={res.boardingRate} onChange={e => updateResource(idx, 'boardingRate', e.target.value)} style={{ width: 70 }} min="0" disabled={isLocked} />
                            <span className="cs-extra-calc">= {fmtScaled(calcBoarding(res, settings), sc, sym)}</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}

            {/* ─── Column totals row ─── */}
            {colVis.periods && (() => {
              const showOff = allocMode === 'mixed' || allocMode === 'offshore';
              const showOn  = allocMode === 'mixed' || allocMode === 'onsite';
              const pc = getPeriodCount(settings);
              const leadingCols  = 5 + (showDetailCols ? 2 : 0); // #, Role, Level, Employee, Skill Group (+Product, FTE if details on)
              // After the M1…Mn cells, count only the columns that are still
              // visible — otherwise the trailing blank cell bloats past the
              // end of the table.
              const postMonthCols =
                  (colVis.hours   ? (showOff ? 1 : 0) + (showOn ? 1 : 0) : 0)
                + (colVis.travels ? 1 : 0)
                + (colVis.rate    ? (showOff ? 1 : 0) + (showOn ? 1 : 0) : 0)
                + (colVis.amount  ? (showOff ? 1 : 0) + (showOn ? 1 : 0) : 0)
                + (colVis.totalAmt   ? 1 : 0)
                + (colVis.extras     ? 1 : 0)
                + (colVis.grandTotal ? 1 : 0)
                + (showCostCols    ? 4 : 0)
                + (showDetailCols  ? 2 : 0) // Price Type, Source (end)
                + 1;                       // actions
              const offTotals = Array.from({ length: pc }, (_, mi) =>
                resources.reduce((a, r) => a + (parseFloat(r.offshoreMonths?.[mi]) || 0), 0)
              );
              const onTotals = Array.from({ length: pc }, (_, mi) =>
                resources.reduce((a, r) => a + (parseFloat(r.onsiteMonths?.[mi]) || 0), 0)
              );
              const fmtTot = v => (v === 0 ? '—' : (Math.round(v * 100) / 100).toString());
              return (
                <tr className="cs-totals-row">
                  <td colSpan={leadingCols} style={{ textAlign: 'right', fontWeight: 700, color: '#94A3B8', padding: '8px 10px' }}>
                    Column Totals →
                  </td>
                  {showOff && offTotals.map((t, mi) => (
                    <td key={`ot${mi}`} className="cs-td-offshore" style={{ textAlign: 'center', fontWeight: 700, color: '#60A5FA' }}>
                      {fmtTot(t)}
                    </td>
                  ))}
                  {showOn && onTotals.map((t, mi) => (
                    <td key={`nt${mi}`} className="cs-td-onsite" style={{ textAlign: 'center', fontWeight: 700, color: '#A78BFA' }}>
                      {fmtTot(t)}
                    </td>
                  ))}
                  <td colSpan={postMonthCols}></td>
                </tr>
              );
            })()}
          </tbody>
        </table>
      </div>

      {!isLocked && (
        <div style={{ marginTop: 8, marginBottom: 16 }}>
          <button className="btn btn-secondary" onClick={addResource}>+ Add Resource</button>
        </div>
      )}

      {/* ─── Summary ─── */}
      <div className="cs-summary-bar">
        <div className="cs-summary-item">
          <span>Total Effort ({amtLabel})</span>
          <strong>{fmtNum(toDisplay(totalEffort, sc), scDec)}</strong>
        </div>
        <div className="cs-summary-item">
          <span>Total Extras ({amtLabel})</span>
          <strong>{fmtNum(toDisplay(totalExtras, sc), scDec)}</strong>
        </div>
        <div className="cs-summary-item">
          <span>Grand Total ({amtLabel})</span>
          <strong>{fmtNum(toDisplay(grandTotal, sc), scDec)}</strong>
        </div>
        <div className="cs-summary-item">
          <span>Discount ({settings.discountPct}%)</span>
          <strong style={{ color: '#EF4444' }}>{fmtNum(toDisplay(discount, sc), scDec)}</strong>
        </div>
        <div className="cs-summary-item cs-summary-highlight">
          <span>Proposed Value ({amtLabel})</span>
          <strong>{fmtNum(toDisplay(proposedValue, sc), scDec)}</strong>
        </div>
        {settings.billingCurrency !== settings.displayCurrency && (
          <div className="cs-summary-item">
            <span>In {settings.displayCurrency} ({scLbl ? scLbl : ''}{getCurrencySymbol(settings.displayCurrency)})</span>
            <strong>{fmtNum(toDisplay(displayTotal, sc), scDec)}</strong>
          </div>
        )}
      </div>

      {/* ─── Cost Summary (when cost cols visible) ─── */}
      {showCostCols && (
        <div className="cs-summary-bar cs-cost-summary">
          <div className="cs-summary-item">
            <span>Total Employee Cost ({amtLabel})</span>
            <strong>{fmtNum(toDisplay(totalLineCost, sc), scDec)}</strong>
          </div>
          <div className="cs-summary-item">
            <span>Total Overhead ({settings.overheadPct}%)</span>
            <strong>{fmtNum(toDisplay(totalOverhead, sc), scDec)}</strong>
          </div>
          <div className="cs-summary-item">
            <span>Total Cost ({amtLabel})</span>
            <strong>{fmtNum(toDisplay(totalCostAll, sc), scDec)}</strong>
          </div>
          <div className={`cs-summary-item${totalMargin >= 0 ? ' cs-summary-margin-pos' : ' cs-summary-margin-neg'}`}>
            <span>Margin ({amtLabel})</span>
            <strong>{fmtNum(toDisplay(totalMargin, sc), scDec)}</strong>
          </div>
          {totalEffort > 0 && (
            <div className={`cs-summary-item${totalMargin >= 0 ? ' cs-summary-margin-pos' : ' cs-summary-margin-neg'}`}>
              <span>Margin %</span>
              <strong>{fmtNum((totalMargin / totalEffort) * 100, 1)}%</strong>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
